import { z, ZodIssueCode } from 'zod';
import { cloneDeep, omit, isArray, uniqBy } from 'lodash-es';
import md5 from 'md5';
import { BLOCKS } from '@contentful/rich-text-types';
import { create } from 'zustand';

/** @deprecated will be removed when dropping backward compatibility for old DND */
const INCOMING_EVENTS = {
    RequestEditorMode: 'requestEditorMode',
    RequestReadOnlyMode: 'requestReadOnlyMode',
    ExperienceUpdated: 'componentTreeUpdated',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    ComponentDraggingChanged: 'componentDraggingChanged',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    ComponentDragCanceled: 'componentDragCanceled',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    ComponentDragStarted: 'componentDragStarted',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    ComponentDragEnded: 'componentDragEnded',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    ComponentMoveEnded: 'componentMoveEnded',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    CanvasResized: 'canvasResized',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    SelectComponent: 'selectComponent',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    HoverComponent: 'hoverComponent',
    UpdatedEntity: 'updatedEntity',
    AssembliesAdded: 'assembliesAdded',
    AssembliesRegistered: 'assembliesRegistered',
    /** @deprecated will be removed when dropping backward compatibility for old DND */
    MouseMove: 'mouseMove',
    RequestedEntities: 'REQUESTED_ENTITIES',
};
/**
 * These modes are ONLY intended to be internally used within the context of
 * editing an experience inside of Contentful Studio. i.e. these modes
 * intentionally do not include preview/delivery modes.
 */
var StudioCanvasMode;
(function (StudioCanvasMode) {
    StudioCanvasMode["READ_ONLY"] = "readOnlyMode";
    StudioCanvasMode["EDITOR"] = "editorMode";
    StudioCanvasMode["NONE"] = "none";
})(StudioCanvasMode || (StudioCanvasMode = {}));
const CONTENTFUL_COMPONENTS = {
    section: {
        id: 'contentful-section',
        name: 'Section',
    },
    container: {
        id: 'contentful-container',
        name: 'Container',
    },
    columns: {
        id: 'contentful-columns',
        name: 'Columns',
    },
    singleColumn: {
        id: 'contentful-single-column',
        name: 'Column',
    },
    button: {
        id: 'contentful-button',
        name: 'Button',
    },
    heading: {
        id: 'contentful-heading',
        name: 'Heading',
    },
    image: {
        id: 'contentful-image',
        name: 'Image',
    },
    richText: {
        id: 'contentful-richText',
        name: 'Rich Text',
    },
    text: {
        id: 'contentful-text',
        name: 'Text',
    },
    divider: {
        id: 'contentful-divider',
        name: 'Divider',
    },
    carousel: {
        id: 'contentful-carousel',
        name: 'Carousel',
    },
};
const ASSEMBLY_NODE_TYPE = 'assembly';
const ASSEMBLY_DEFAULT_CATEGORY = 'Assemblies';
const ASSEMBLY_BLOCK_NODE_TYPE = 'assemblyBlock';
const CF_STYLE_ATTRIBUTES = [
    'cfVisibility',
    'cfHorizontalAlignment',
    'cfVerticalAlignment',
    'cfMargin',
    'cfPadding',
    'cfBackgroundColor',
    'cfWidth',
    'cfMaxWidth',
    'cfHeight',
    'cfImageAsset',
    'cfImageOptions',
    'cfBackgroundImageUrl',
    'cfBackgroundImageOptions',
    'cfFlexDirection',
    'cfFlexWrap',
    'cfFlexReverse',
    'cfBorder',
    'cfBorderRadius',
    'cfGap',
    'cfColumnSpan',
    'cfColumnSpanLock',
    'cfColumns',
    'cfFontSize',
    'cfFontWeight',
    'cfLineHeight',
    'cfLetterSpacing',
    'cfTextColor',
    'cfTextAlign',
    'cfTextTransform',
    'cfTextBold',
    'cfTextItalic',
    'cfTextUnderline',
];
const EMPTY_CONTAINER_SIZE = '80px';
var PostMessageMethods;
(function (PostMessageMethods) {
    PostMessageMethods["REQUEST_ENTITIES"] = "REQUEST_ENTITIES";
    PostMessageMethods["REQUESTED_ENTITIES"] = "REQUESTED_ENTITIES";
})(PostMessageMethods || (PostMessageMethods = {}));
const SUPPORTED_IMAGE_FORMATS = ['jpg', 'png', 'webp', 'gif', 'avif'];
const SIDELOADED_PREFIX = 'sideloaded_';

const structureComponentIds = new Set([
    CONTENTFUL_COMPONENTS.section.id,
    CONTENTFUL_COMPONENTS.columns.id,
    CONTENTFUL_COMPONENTS.container.id,
    CONTENTFUL_COMPONENTS.singleColumn.id,
]);
const patternTypes = new Set([ASSEMBLY_NODE_TYPE, ASSEMBLY_BLOCK_NODE_TYPE]);
const allContentfulComponentIds = new Set(Object.values(CONTENTFUL_COMPONENTS).map((component) => component.id));
const isPatternComponent = (type) => patternTypes.has(type ?? '');
const isContentfulStructureComponent = (componentId) => structureComponentIds.has((componentId ?? ''));
const isContentfulComponent = (componentId) => allContentfulComponentIds.has((componentId ?? ''));
const isComponentAllowedOnRoot = ({ type, category, componentId }) => isPatternComponent(type) ||
    category === ASSEMBLY_DEFAULT_CATEGORY ||
    isContentfulStructureComponent(componentId) ||
    componentId === CONTENTFUL_COMPONENTS.divider.id;
const isStructureWithRelativeHeight = (componentId, height) => {
    return isContentfulStructureComponent(componentId) && !height?.toString().endsWith('px');
};

// These styles get added to every component, user custom or contentful provided
const builtInStyles = {
    cfVerticalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align left',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align right',
                },
                {
                    value: 'stretch',
                    displayName: 'Stretch',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The vertical alignment of the section',
        defaultValue: 'center',
        displayName: 'Vertical alignment',
    },
    cfHorizontalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align top',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align bottom',
                },
                {
                    value: 'stretch',
                    displayName: 'Stretch',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The horizontal alignment of the section',
        defaultValue: 'center',
        displayName: 'Horizontal alignment',
    },
    cfVisibility: {
        displayName: 'Visibility toggle',
        type: 'Boolean',
        group: 'style',
        defaultValue: true,
        description: 'The visibility of the component',
    },
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the section',
        defaultValue: '0 0 0 0',
    },
    cfPadding: {
        displayName: 'Padding',
        type: 'Text',
        group: 'style',
        description: 'The padding of the section',
        defaultValue: '0 0 0 0',
    },
    cfBackgroundColor: {
        displayName: 'Background color',
        type: 'Text',
        group: 'style',
        description: 'The background color of the section',
        defaultValue: 'rgba(0, 0, 0, 0)',
    },
    cfWidth: {
        displayName: 'Width',
        type: 'Text',
        group: 'style',
        description: 'The width of the section',
        defaultValue: '100%',
    },
    cfHeight: {
        displayName: 'Height',
        type: 'Text',
        group: 'style',
        description: 'The height of the section',
        defaultValue: 'fit-content',
    },
    cfMaxWidth: {
        displayName: 'Max width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the section',
        defaultValue: 'none',
    },
    cfFlexDirection: {
        displayName: 'Direction',
        type: 'Text',
        group: 'style',
        description: 'The orientation of the section',
        defaultValue: 'column',
    },
    cfFlexReverse: {
        displayName: 'Reverse Direction',
        type: 'Boolean',
        group: 'style',
        description: 'Toggle the flex direction to be reversed',
        defaultValue: false,
    },
    cfFlexWrap: {
        displayName: 'Wrap objects',
        type: 'Text',
        group: 'style',
        description: 'Wrap objects',
        defaultValue: 'nowrap',
    },
    cfBorder: {
        displayName: 'Border',
        type: 'Text',
        group: 'style',
        description: 'The border of the section',
        defaultValue: '0px solid rgba(0, 0, 0, 0)',
    },
    cfGap: {
        displayName: 'Gap',
        type: 'Text',
        group: 'style',
        description: 'The spacing between the elements of the section',
        defaultValue: '0px',
    },
    cfHyperlink: {
        displayName: 'URL',
        type: 'Hyperlink',
        defaultValue: '',
        validations: {
            format: 'URL',
            bindingSourceType: ['entry', 'experience', 'manual'],
        },
        description: 'hyperlink for section or container',
    },
    cfOpenInNewTab: {
        displayName: 'URL behaviour',
        type: 'Boolean',
        defaultValue: false,
        description: 'Open in new tab',
    },
};
const optionalBuiltInStyles = {
    cfFontSize: {
        displayName: 'Font Size',
        type: 'Text',
        group: 'style',
        description: 'The font size of the element',
        defaultValue: '16px',
    },
    cfFontWeight: {
        validations: {
            in: [
                {
                    value: '400',
                    displayName: 'Normal',
                },
                {
                    value: '500',
                    displayName: 'Medium',
                },
                {
                    value: '600',
                    displayName: 'Semi Bold',
                },
            ],
        },
        displayName: 'Font Weight',
        type: 'Text',
        group: 'style',
        description: 'The font weight of the element',
        defaultValue: '400',
    },
    cfImageAsset: {
        displayName: 'Image',
        type: 'Media',
        description: 'Image to display',
        validations: {
            bindingSourceType: ['entry', 'asset', 'manual'],
        },
    },
    cfImageOptions: {
        displayName: 'Image options',
        type: 'Object',
        group: 'style',
        defaultValue: {
            width: '100%',
            height: '100%',
            targetSize: 'unset',
        },
    },
    cfBackgroundColor: {
        displayName: 'Background color',
        type: 'Text',
        group: 'style',
        description: 'The background color of the element',
        defaultValue: 'rgba(0, 0, 0, 0)',
    },
    cfBackgroundImageUrl: {
        displayName: 'Background image',
        type: 'Media',
        description: 'Background image for component',
        validations: {
            bindingSourceType: ['entry', 'asset', 'manual'],
        },
    },
    cfBackgroundImageOptions: {
        displayName: 'Background image options',
        type: 'Object',
        group: 'style',
        defaultValue: {
            scaling: 'fill',
            alignment: 'left top',
            targetSize: '2000px',
        },
    },
    cfBorderRadius: {
        displayName: 'Border Radius',
        type: 'Text',
        group: 'style',
        description: 'The border radius of the section',
        defaultValue: '0px',
    },
    cfLineHeight: {
        displayName: 'Line Height',
        type: 'Text',
        group: 'style',
        description: 'The line height of the element',
        defaultValue: '20px',
    },
    cfLetterSpacing: {
        displayName: 'Letter Spacing',
        type: 'Text',
        group: 'style',
        description: 'The letter spacing of the element',
        defaultValue: '0px',
    },
    cfTextColor: {
        displayName: 'Text Color',
        type: 'Text',
        group: 'style',
        description: 'The text color of the element',
        defaultValue: 'rgba(0, 0, 0, 1)',
    },
    cfTextAlign: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align start',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align end',
                },
            ],
        },
        displayName: 'Text Align',
        type: 'Text',
        group: 'style',
        description: 'The text alignment of the element',
        defaultValue: 'start',
    },
    cfTextTransform: {
        validations: {
            in: [
                {
                    value: 'none',
                    displayName: 'Normal',
                },
                {
                    value: 'capitalize',
                    displayName: 'Capitalize',
                },
                {
                    value: 'uppercase',
                    displayName: 'Uppercase',
                },
                {
                    value: 'lowercase',
                    displayName: 'Lowercase',
                },
            ],
        },
        displayName: 'Text Transform',
        type: 'Text',
        group: 'style',
        description: 'The text transform of the element',
        defaultValue: 'none',
    },
    cfTextBold: {
        displayName: 'Bold',
        type: 'Boolean',
        group: 'style',
        description: 'The text bold of the element',
        defaultValue: false,
    },
    cfTextItalic: {
        displayName: 'Italic',
        type: 'Boolean',
        group: 'style',
        description: 'The text italic of the element',
        defaultValue: false,
    },
    cfTextUnderline: {
        displayName: 'Underline',
        type: 'Boolean',
        group: 'style',
        description: 'The text underline of the element',
        defaultValue: false,
    },
};
const sectionBuiltInStyles = {
    ...builtInStyles,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
};
const containerBuiltInStyles = {
    ...builtInStyles,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the container',
        // Note: The UI overwrites '0 Auto 0 Auto' as the default value for top-level containers
        defaultValue: '0 0 0 0',
    },
    cfMaxWidth: {
        displayName: 'Max Width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the section',
        defaultValue: '1192px',
    },
};
const dividerBuiltInStyles = {
    cfVisibility: builtInStyles.cfVisibility,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the divider',
        defaultValue: '20px 0px 20px 0px',
    },
    cfWidth: {
        displayName: 'Width',
        type: 'Text',
        group: 'style',
        description: 'The width of the divider',
        defaultValue: '100%',
    },
    cfHeight: {
        displayName: 'Height',
        type: 'Text',
        group: 'style',
        description: 'The height of the divider',
        defaultValue: '1px',
    },
    cfMaxWidth: {
        displayName: 'Max width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the divider',
        defaultValue: 'none',
    },
    cfBackgroundColor: {
        displayName: 'Background color',
        type: 'Text',
        group: 'style',
        description: 'The background color of the divider',
        defaultValue: 'rgba(204, 204, 204, 1)',
    },
};
const singleColumnBuiltInStyles = {
    cfVisibility: builtInStyles.cfVisibility,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundColor: optionalBuiltInStyles.cfBackgroundColor,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
    cfVerticalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align left',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align right',
                },
                {
                    value: 'stretch',
                    displayName: 'Stretch',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The vertical alignment of the column',
        defaultValue: 'center',
        displayName: 'Vertical alignment',
    },
    cfHorizontalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align top',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align bottom',
                },
                {
                    value: 'stretch',
                    displayName: 'Stretch',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The horizontal alignment of the column',
        defaultValue: 'center',
        displayName: 'Horizontal alignment',
    },
    cfPadding: {
        displayName: 'Padding',
        type: 'Text',
        group: 'style',
        description: 'The padding of the column',
        defaultValue: '0 0 0 0',
    },
    cfFlexDirection: {
        displayName: 'Direction',
        type: 'Text',
        group: 'style',
        description: 'The orientation of the column',
        defaultValue: 'column',
    },
    cfFlexWrap: {
        displayName: 'Wrap objects',
        type: 'Text',
        group: 'style',
        description: 'Wrap objects',
        defaultValue: 'nowrap',
    },
    cfBorder: {
        displayName: 'Border',
        type: 'Text',
        group: 'style',
        description: 'The border of the column',
        defaultValue: '0px solid rgba(0, 0, 0, 0)',
    },
    cfGap: {
        displayName: 'Gap',
        type: 'Text',
        group: 'style',
        description: 'The spacing between the elements of the column',
        defaultValue: '0px',
    },
    cfColumnSpan: {
        type: 'Text',
        defaultValue: '6',
        group: 'style',
    },
    cfColumnSpanLock: {
        type: 'Boolean',
        defaultValue: false,
        group: 'style',
    },
};
const columnsBuiltInStyles = {
    cfVisibility: builtInStyles.cfVisibility,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundColor: optionalBuiltInStyles.cfBackgroundColor,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the columns',
        // Note: The UI overwrites '0 Auto 0 Auto' as the default value for top-level columns
        defaultValue: '0 0 0 0',
    },
    cfWidth: {
        displayName: 'Width',
        type: 'Text',
        group: 'style',
        description: 'The width of the columns',
        defaultValue: '100%',
    },
    cfMaxWidth: {
        displayName: 'Max width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the columns',
        defaultValue: '1192px',
    },
    cfPadding: {
        displayName: 'Padding',
        type: 'Text',
        group: 'style',
        description: 'The padding of the columns',
        defaultValue: '10px 10px 10px 10px',
    },
    cfBorder: {
        displayName: 'Border',
        type: 'Text',
        group: 'style',
        description: 'The border of the columns',
        defaultValue: '0px solid rgba(0, 0, 0, 0)',
    },
    cfGap: {
        displayName: 'Gap',
        type: 'Text',
        group: 'style',
        description: 'The spacing between the elements of the columns',
        defaultValue: '10px 10px',
    },
    cfColumns: {
        type: 'Text',
        defaultValue: '[6,6]',
        group: 'style',
    },
    cfWrapColumns: {
        type: 'Boolean',
        defaultValue: false,
        group: 'style',
    },
    cfWrapColumnsCount: {
        type: 'Text',
        defaultValue: '2',
        group: 'style',
    },
};

let designTokensRegistry = {};
// This function is used to ensure that the composite values are valid since composite values are optional.
// Therefore only border and in the future text related design tokens are/will be checked in this funciton.
// Ensuring values for simple key-value design tokens are not neccessary since they are required via typescript.
const ensureValidCompositeValues = (designTokenDefinition) => {
    // Text token validation
    if (designTokenDefinition.text) {
        for (const textKey in designTokenDefinition.text) {
            const textValue = designTokenDefinition.text[textKey];
            designTokenDefinition.text[textKey] = {
                emphasis: textValue.emphasis || 'none',
                fontSize: textValue.fontSize || '16px',
                case: textValue.case || 'normal',
                fontWeight: textValue.fontWeight || '400',
                lineHeight: textValue.lineHeight || '20px',
                letterSpacing: textValue.letterSpacing || '0px',
                color: textValue.color || '#000000',
            };
        }
    }
    // Border validation
    if (designTokenDefinition.border) {
        for (const borderKey in designTokenDefinition.border) {
            const borderValue = designTokenDefinition.border[borderKey];
            designTokenDefinition.border[borderKey] = {
                width: borderValue.width || '1px',
                style: borderValue.style || 'solid',
                color: borderValue.color || '#000000',
            };
        }
    }
    return designTokenDefinition;
};
/**
 * Register design tokens styling
 * @param designTokenDefinition - {[key:string]: Record<string, string>}
 * @returns void
 */
const defineDesignTokens = (designTokenDefinition) => {
    Object.assign(designTokensRegistry, ensureValidCompositeValues(designTokenDefinition));
};
const templateStringRegex = /\${(\w.+?)}/g;
const getDesignTokenRegistration = (breakpointValue, variableName) => {
    if (!breakpointValue)
        return breakpointValue;
    let resolvedValue = '';
    // Match all parts of the string, including design tokens and other parts
    const parts = breakpointValue.match(/(\${.+?}|\S+)/g);
    if (parts) {
        for (const part of parts) {
            const isDesignToken = templateStringRegex.test(part);
            const tokenValue = isDesignToken ? resolveSimpleDesignToken(part, variableName) : part;
            resolvedValue += `${tokenValue} `;
        }
    }
    // Not trimming would end up with a trailing space that breaks the check in `calculateNodeDefaultHeight`
    return resolvedValue.trim();
};
const resolveSimpleDesignToken = (templateString, variableName) => {
    const nonTemplateValue = templateString.replace(templateStringRegex, '$1');
    const [tokenCategory, tokenName] = nonTemplateValue.split('.');
    const tokenValues = designTokensRegistry[tokenCategory];
    if (tokenValues && tokenValues[tokenName]) {
        if (variableName === 'cfBorder') {
            const { width, style, color } = tokenValues[tokenName];
            return `${width} ${style} ${color}`;
        }
        return tokenValues[tokenName];
    }
    if (builtInStyles[variableName]) {
        return builtInStyles[variableName].defaultValue;
    }
    if (optionalBuiltInStyles[variableName]) {
        return optionalBuiltInStyles[variableName].defaultValue;
    }
    return '0px';
};
// Used in unit tests to reset the design token registry
const resetDesignTokenRegistry = () => {
    designTokensRegistry = {};
};

// If more than one version is supported, use z.union
const SchemaVersions = z.literal('2023-09-28');
// Keep deprecated versions here just for reference
z.union([
    z.literal('2023-08-23'),
    z.literal('2023-07-26'),
    z.literal('2023-06-27'),
]);

const DefinitionPropertyTypeSchema = z.enum([
    'Text',
    'RichText',
    'Number',
    'Date',
    'Boolean',
    'Location',
    'Media',
    'Object',
    'Hyperlink',
    'Array',
    'Link',
]);
const DefinitionPropertyKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,32}$/, { message: 'Property needs to match: /^[a-zA-Z0-9-_]{1,32}$/' });
const PrimitiveValueSchema = z.union([
    z.string(),
    z.boolean(),
    z.number(),
    z.record(z.any(), z.any()),
    z.undefined(),
]);
const UsedComponentsSchema = z.array(z.object({
    sys: z.object({
        type: z.literal('Link'),
        id: z.string(),
        linkType: z.literal('Entry'),
    }),
}));
const uuidKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,21}$/, { message: 'Does not match /^[a-zA-Z0-9-_]{1,21}$/' });
const DataSourceSchema = z.record(uuidKeySchema, z.object({
    sys: z.object({
        type: z.literal('Link'),
        id: z.string(),
        linkType: z.enum(['Entry', 'Asset']),
    }),
}));
const UnboundValuesSchema = z.record(uuidKeySchema, z.object({
    value: PrimitiveValueSchema,
}));
/**
 * Property keys for imported components have a limit of 32 characters (to be implemented) while
 * property keys for patterns have a limit of 54 characters (<32-char-variable-name>_<21-char-nanoid-id>).
 * Because we cannot distinguish between the two in the componentTree, we will use the larger limit for both.
 */
const propertyKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,54}$/, { message: 'Does not match /^[a-zA-Z0-9-_]{1,54}$/' });
const ComponentTreeNodeIdSchema = z
    .string()
    .regex(/^[a-zA-Z0-9]{1,8}$/, { message: 'Does not match /^[a-zA-Z0-9]{1,8}$/' });
const breakpointsRefinement = (value, ctx) => {
    if (!value.length || value[0].query !== '*') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `The first breakpoint should include the following attributes: { "query": "*" }`,
        });
        return;
    }
    // Return early if there's only one generic breakpoint
    const hasNoBreakpointsStrategy = value.length === 1;
    if (hasNoBreakpointsStrategy) {
        return;
    }
    // Check if any breakpoint id occurs twice
    const ids = value.map((breakpoint) => breakpoint.id);
    const hasDuplicateIds = new Set(ids).size !== ids.length;
    if (hasDuplicateIds) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Breakpoint IDs must be unique`,
        });
        return;
    }
    // Skip the first one which is guaranteed to be a wildcard query
    const nonBaseBreakpoints = value.slice(1);
    const isMobileFirstStrategy = nonBaseBreakpoints[0].query.startsWith('>');
    const isDesktopFirstStrategy = nonBaseBreakpoints[0].query.startsWith('<');
    if (isMobileFirstStrategy) {
        const areOperatorsEqual = nonBaseBreakpoints.every(({ query }) => query.startsWith('>'));
        if (!areOperatorsEqual) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Breakpoint queries must be in the format ">[size]px" for mobile-first strategy`,
            });
        }
        // Extract the queries boundary by removing the special characters around it
        const queries = nonBaseBreakpoints.map((bp) => parseInt(bp.query.replace(/px|<|>/, '')));
        // Starting with the third breakpoint, check that every query is higher than the one above
        const isIncreasing = queries.every((value, index, array) => index === 0 || value > array[index - 1]);
        if (!isIncreasing) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `When using a mobile-first strategy, all breakpoints must have strictly increasing pixel values`,
            });
        }
    }
    else if (isDesktopFirstStrategy) {
        const areOperatorsEqual = nonBaseBreakpoints.every(({ query }) => query.startsWith('<'));
        if (!areOperatorsEqual) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Breakpoint queries must be in the format "<[size]px" for desktop-first strategy`,
            });
        }
        // Extract the queries boundary by removing the special characters around it
        const queries = nonBaseBreakpoints.map((bp) => parseInt(bp.query.replace(/px|<|>/, '')));
        // Starting with the third breakpoint, check that every query is lower than the one above
        const isDecreasing = queries.every((value, index, array) => index === 0 || value < array[index - 1]);
        if (!isDecreasing) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `When using a desktop-first strategy, all breakpoints must have strictly decreasing pixel values`,
            });
        }
    }
    else if (!isMobileFirstStrategy && !isDesktopFirstStrategy) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `You may only use a mobile-first or desktop-first strategy for breakpoints using '<' or '>' queries`,
        });
    }
};
const ValuesByBreakpointSchema = z.record(z.lazy(() => PrimitiveValueSchema));
const BindingSourceTypeEnumSchema = z
    .array(z.enum(['entry', 'asset', 'manual', 'experience']))
    .nonempty();
const DesignValueSchema = z
    .object({
    type: z.literal('DesignValue'),
    valuesByBreakpoint: ValuesByBreakpointSchema,
})
    .strict();
const BoundValueSchema = z
    .object({
    type: z.literal('BoundValue'),
    path: z.string(),
})
    .strict();
const HyperlinkValueSchema = z
    .object({
    type: z.literal('HyperlinkValue'),
    linkTargetKey: z.string(),
    /** Allows to override parts of the URL, e.g. the locale */
    overrides: z.object({}).optional(),
})
    .strict();
const UnboundValueSchema = z
    .object({
    type: z.literal('UnboundValue'),
    key: z.string(),
})
    .strict();
const ComponentValueSchema = z
    .object({
    type: z.literal('ComponentValue'),
    key: z.string(),
})
    .strict();
const NoValueSchema = z.object({ type: z.literal('NoValue') }).strict();
const ComponentPropertyValueSchema = z.discriminatedUnion('type', [
    DesignValueSchema,
    BoundValueSchema,
    UnboundValueSchema,
    HyperlinkValueSchema,
    ComponentValueSchema,
    NoValueSchema,
]);
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const ParameterSchema = z.object({
    type: z.literal('BoundValue'),
    path: z.string(),
});
const ParametersSchema = z.record(propertyKeySchema, ParameterSchema);
const BREAKPOINT_QUERY_REGEX = /^\*$|^[<>][0-9]+px$/;
const BreakpointSchema = z
    .object({
    id: propertyKeySchema,
    // Can be replace with z.templateLiteral when upgrading to zod v4
    query: z.string().refine((s) => BREAKPOINT_QUERY_REGEX.test(s)),
    previewSize: z.string(),
    displayName: z.string(),
    displayIcon: z.enum(['desktop', 'tablet', 'mobile']).optional(),
})
    .strict();
// Use helper schema to define a recursive schema with its type correctly below
const BaseComponentTreeNodeSchema = z.object({
    id: ComponentTreeNodeIdSchema.optional(),
    definitionId: DefinitionPropertyKeySchema,
    prebindingId: z.string().optional(),
    displayName: z.string().optional(),
    slotId: z.string().optional(),
    variables: z.record(propertyKeySchema, ComponentPropertyValueSchema),
    parameters: ParametersSchema.optional(),
});
const ComponentVariableSchema = z.object({
    displayName: z.string().optional(),
    type: DefinitionPropertyTypeSchema,
    description: z.string().optional(),
    group: z.string().optional(),
    defaultValue: PrimitiveValueSchema.or(ComponentPropertyValueSchema).optional(),
    validations: z
        .object({
        bindingSourceType: BindingSourceTypeEnumSchema.optional(),
        required: z.boolean().optional(),
        format: z.literal('URL').optional(),
        in: z
            .array(z.object({
            value: z.union([z.string(), z.number()]),
            displayName: z.string().optional(),
        }))
            .optional(),
    })
        .optional(),
});
const ComponentTreeNodeSchema = BaseComponentTreeNodeSchema.extend({
    children: z.lazy(() => ComponentTreeNodeSchema.array()),
});
const ComponentTreeSchema = z
    .object({
    breakpoints: z.array(BreakpointSchema).superRefine(breakpointsRefinement),
    children: z.array(ComponentTreeNodeSchema),
    schemaVersion: SchemaVersions,
})
    .strict();
const localeWrapper = (fieldSchema) => z.record(z.string(), fieldSchema);

z.object({
    componentTree: localeWrapper(ComponentTreeSchema),
    dataSource: localeWrapper(DataSourceSchema),
    unboundValues: localeWrapper(UnboundValuesSchema),
    usedComponents: localeWrapper(UsedComponentsSchema).optional(),
});

const THUMBNAIL_IDS = [
    'columns',
    'columnsPlusRight',
    'imagesSquare',
    'subtitles',
    'rowsPlusBottom',
    'userRectangle',
    'textbox',
    'monitorPlay',
    'article',
    'table',
    'star',
    'heartStraight',
    'frameCorners',
    'rows',
    'dotsThreeOutline',
    'listDashes',
    'checkerBoard',
    'gridFour',
    'slideshow',
    'diamondsFour',
    'cards',
    'textColumns',
    'duplex',
];
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const VariableMappingSchema = z.object({
    parameterId: propertyKeySchema,
    type: z.literal('ContentTypeMapping'),
    pathsByContentType: z.record(z.string(), z.object({ path: z.string() })),
});
const PassToNodeSchema = z
    .object({
    nodeId: propertyKeySchema,
    parameterId: propertyKeySchema,
    prebindingId: propertyKeySchema,
})
    .strict();
const ParameterDefinitionSchema = z.object({
    defaultSource: z
        .strictObject({
        type: z.enum(['Entry']),
        contentTypeId: z.string(),
        link: z.strictObject({
            sys: z.strictObject({
                type: z.literal('Link'),
                id: z.string(),
                linkType: z.enum(['Entry']),
            }),
        }),
    })
        .optional(),
    contentTypes: z.array(z.string()).min(1),
    passToNodes: z.array(PassToNodeSchema).optional(),
});
const ParameterDefinitionsSchema = z.record(propertyKeySchema, ParameterDefinitionSchema);
const VariableMappingsSchema = z.record(propertyKeySchema, VariableMappingSchema);
const ComponentVariablesSchema = z.record(z.string().regex(/^[a-zA-Z0-9-_]{1,54}$/), // Here the key is <variableName>_<nanoidId> so we need to allow for a longer length
ComponentVariableSchema);
const PrebindingDefinitionSchema = z
    .object({
    id: propertyKeySchema,
    parameterDefinitions: ParameterDefinitionsSchema,
    variableMappings: VariableMappingsSchema.optional(),
    allowedVariableOverrides: z.array(z.string()).optional(),
})
    .strict();
const ComponentSettingsSchema = z
    .object({
    variableDefinitions: ComponentVariablesSchema,
    thumbnailId: z.enum(THUMBNAIL_IDS).optional(),
    category: z.string().max(50, 'Category must contain at most 50 characters').optional(),
    prebindingDefinitions: z.array(PrebindingDefinitionSchema).length(1).optional(),
})
    .strict();
z.object({
    componentTree: localeWrapper(ComponentTreeSchema),
    dataSource: localeWrapper(DataSourceSchema),
    unboundValues: localeWrapper(UnboundValuesSchema),
    usedComponents: localeWrapper(UsedComponentsSchema).optional(),
    componentSettings: localeWrapper(ComponentSettingsSchema),
});

z.object({
    id: DefinitionPropertyKeySchema,
    variables: z.record(DefinitionPropertyKeySchema, ComponentVariableSchema.extend({
        defaultValue: PrimitiveValueSchema.optional(),
    }).superRefine((val, ctx) => {
        switch (val.type) {
            case 'Array':
                if (typeof val.defaultValue !== 'undefined') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue is not supported for "Array" type for ${ctx.path.join('.')}`,
                        fatal: false,
                    });
                }
                break;
            case 'Boolean':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'boolean') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a boolean when type is "Boolean" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Date':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Date" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Hyperlink':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Hyperlink" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Link':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue is not supported for "Link" type for ${ctx.path.join('.')}`,
                        fatal: false,
                    });
                }
                break;
            case 'Location':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "Location" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Media':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Media" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Number':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'number') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a number when type is "Number" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Object':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "Object" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'RichText':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "RichText" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Text':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Text" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
        }
    })),
});

var CodeNames;
(function (CodeNames) {
    CodeNames["Type"] = "type";
    CodeNames["Required"] = "required";
    CodeNames["Unexpected"] = "unexpected";
    CodeNames["Regex"] = "regex";
    CodeNames["In"] = "in";
    CodeNames["Size"] = "size";
    CodeNames["Custom"] = "custom";
})(CodeNames || (CodeNames = {}));
const convertInvalidType = (issue) => {
    const name = issue.received === 'undefined' ? CodeNames.Required : CodeNames.Type;
    const details = issue.received === 'undefined'
        ? `The property "${issue.path.slice(-1)}" is required here`
        : `The type of "${issue.path.slice(-1)}" is incorrect, expected type: ${issue.expected}`;
    return {
        details: details,
        name: name,
        path: issue.path,
        value: issue.received.toString(),
    };
};
const convertUnrecognizedKeys = (issue) => {
    const missingProperties = issue.keys.map((k) => `"${k}"`).join(', ');
    return {
        details: issue.keys.length > 1
            ? `The properties ${missingProperties} are not expected`
            : `The property ${missingProperties} is not expected`,
        name: CodeNames.Unexpected,
        path: issue.path,
    };
};
const convertInvalidString = (issue) => {
    return {
        details: issue.message || 'Invalid string',
        name: issue.validation === 'regex' ? CodeNames.Regex : CodeNames.Unexpected,
        path: issue.path,
    };
};
const convertInvalidEnumValue = (issue) => {
    return {
        details: issue.message || 'Value must be one of expected values',
        name: CodeNames.In,
        path: issue.path,
        value: issue.received.toString(),
        expected: issue.options,
    };
};
const convertInvalidLiteral = (issue) => {
    return {
        details: issue.message || 'Value must be one of expected values',
        name: CodeNames.In,
        path: issue.path,
        value: issue.received,
        expected: [issue.expected],
    };
};
const convertTooBig = (issue) => {
    return {
        details: issue.message || `Size should be at most ${issue.maximum}`,
        name: CodeNames.Size,
        path: issue.path,
        max: issue.maximum,
    };
};
const convertTooSmall = (issue) => {
    return {
        details: issue.message || `Size should be at least ${issue.minimum}`,
        name: CodeNames.Size,
        path: issue.path,
        min: issue.minimum,
    };
};
const defaultConversion = (issue) => {
    return {
        details: issue.message || 'An unexpected error occurred',
        name: CodeNames.Custom,
        path: issue.path.map(String),
    };
};
const zodToContentfulError = (issue) => {
    switch (issue.code) {
        case ZodIssueCode.invalid_type:
            return convertInvalidType(issue);
        case ZodIssueCode.unrecognized_keys:
            return convertUnrecognizedKeys(issue);
        case ZodIssueCode.invalid_enum_value:
            return convertInvalidEnumValue(issue);
        case ZodIssueCode.invalid_string:
            return convertInvalidString(issue);
        case ZodIssueCode.too_small:
            return convertTooSmall(issue);
        case ZodIssueCode.too_big:
            return convertTooBig(issue);
        case ZodIssueCode.invalid_literal:
            return convertInvalidLiteral(issue);
        default:
            return defaultConversion(issue);
    }
};

const validateBreakpointsDefinition = (breakpoints) => {
    const result = z
        .array(BreakpointSchema)
        .superRefine(breakpointsRefinement)
        .safeParse(breakpoints);
    if (!result.success) {
        return {
            success: false,
            errors: result.error.issues.map(zodToContentfulError),
        };
    }
    return { success: true };
};

let breakpointsRegistry = [];
/**
 * Register custom breakpoints
 * @param breakpoints - [{[key:string]: string}]
 * @returns void
 */
const defineBreakpoints = (breakpoints) => {
    Object.assign(breakpointsRegistry, breakpoints);
};
const runBreakpointsValidation = () => {
    if (!breakpointsRegistry.length)
        return;
    const validation = validateBreakpointsDefinition(breakpointsRegistry);
    if (!validation.success) {
        throw new Error(`Invalid breakpoints definition. Failed with errors: \n${JSON.stringify(validation.errors, null, 2)}`);
    }
};
// Used in the tests to get a breakpoint registration
const getBreakpointRegistration = (id) => breakpointsRegistry.find((breakpoint) => breakpoint.id === id);
// Used in the tests to reset the registry
const resetBreakpointsRegistry = () => {
    breakpointsRegistry = [];
};

const sdkOptionsRegistry = {};
/**
 * Used inside defineComponents to forward registry arguments to this registry
 * of SDK options.
 */
const defineSdkOptions = (options) => {
    Object.assign(sdkOptionsRegistry, options);
};
const getSdkOptions = () => {
    return { ...sdkOptionsRegistry };
};

const MEDIA_QUERY_REGEXP = /(<|>)(\d{1,})(px|cm|mm|in|pt|pc)$/;
const toCSSMediaQuery = ({ query }) => {
    if (query === '*')
        return undefined;
    const match = query.match(MEDIA_QUERY_REGEXP);
    if (!match)
        return undefined;
    const [, operator, value, unit] = match;
    if (operator === '<') {
        const maxScreenWidth = Number(value) - 1;
        return `(max-width: ${maxScreenWidth}${unit})`;
    }
    else if (operator === '>') {
        const minScreenWidth = Number(value) + 1;
        return `(min-width: ${minScreenWidth}${unit})`;
    }
    return undefined;
};
// Remove this helper when upgrading to TypeScript 5.0 - https://github.com/microsoft/TypeScript/issues/48829
const findLast = (array, predicate) => {
    return [...array].reverse().find(predicate);
};
// Initialise media query matchers. This won't include the always matching fallback breakpoint.
const mediaQueryMatcher = (breakpoints) => {
    const mediaQueryMatches = {};
    const mediaQueryMatchers = breakpoints
        .map((breakpoint) => {
        const cssMediaQuery = toCSSMediaQuery(breakpoint);
        if (!cssMediaQuery)
            return undefined;
        if (typeof window === 'undefined')
            return undefined;
        const mediaQueryMatcher = window.matchMedia(cssMediaQuery);
        mediaQueryMatches[breakpoint.id] = mediaQueryMatcher.matches;
        return { id: breakpoint.id, signal: mediaQueryMatcher };
    })
        .filter((matcher) => !!matcher);
    return [mediaQueryMatchers, mediaQueryMatches];
};
const getActiveBreakpointIndex = (breakpoints, mediaQueryMatches, fallbackBreakpointIndex) => {
    // The breakpoints are ordered (desktop-first: descending by screen width, mobile-first: ascending by screen width).
    const breakpointsWithMatches = breakpoints.map(({ id }, index) => ({
        id,
        index,
        // The fallback breakpoint with wildcard query will always match
        isMatch: mediaQueryMatches[id] ?? index === fallbackBreakpointIndex,
    }));
    // Find the last breakpoint in the list that matches (desktop-first: the narrowest one, mobile-first: the widest one)
    const mostSpecificIndex = findLast(breakpointsWithMatches, ({ isMatch }) => isMatch)?.index;
    return mostSpecificIndex ?? fallbackBreakpointIndex;
};
const getFallbackBreakpointIndex = (breakpoints) => {
    // The validation ensures that there will be exactly one breakpoint using the wildcard query.
    // If there is none, we just take the first one in the list.
    return Math.max(breakpoints.findIndex(({ query }) => query === '*'), 0);
};
const builtInStylesWithDesignTokens = [
    'cfMargin',
    'cfPadding',
    'cfGap',
    'cfWidth',
    'cfHeight',
    'cfBackgroundColor',
    'cfBorder',
    'cfBorderRadius',
    'cfFontSize',
    'cfLineHeight',
    'cfLetterSpacing',
    'cfTextColor',
    'cfMaxWidth',
];
const isValidBreakpointValue = (value) => {
    return value !== undefined && value !== null && value !== '';
};
const getValueForBreakpoint = (valuesByBreakpoint, breakpoints, activeBreakpointIndex, fallbackBreakpointIndex, 
/** Provide the name for built-in styles to replace design tokens. Supported properties are:
 * cfMargin, cfPadding, cfGap, cfWidth, cfHeight, cfBackgroundColor,
 * cfBorder, cfBorderRadius, cfFontSize, cfLineHeight, cfLetterSpacing,
 * cfTextColor, cfMaxWidth
 */
propertyName, resolveDesignTokens = true) => {
    const eventuallyResolveDesignTokens = (value) => {
        // This is used externally in the web app to determine the original persisted value
        if (!resolveDesignTokens) {
            return value;
        }
        // For some built-in design properties, we support design tokens
        if (propertyName && builtInStylesWithDesignTokens.includes(propertyName)) {
            return getDesignTokenRegistration(value, propertyName);
        }
        // For all other properties, we just return the breakpoint-specific value
        return value;
    };
    if (valuesByBreakpoint instanceof Object) {
        // Assume that the values are sorted by media query to apply the cascading CSS logic
        for (let index = activeBreakpointIndex; index >= 0; index--) {
            const breakpointId = breakpoints[index]?.id;
            if (isValidBreakpointValue(valuesByBreakpoint[breakpointId])) {
                // If the value is defined, we use it and stop the breakpoints cascade
                return eventuallyResolveDesignTokens(valuesByBreakpoint[breakpointId]);
            }
        }
        const fallbackBreakpointId = breakpoints[fallbackBreakpointIndex]?.id;
        if (isValidBreakpointValue(valuesByBreakpoint[fallbackBreakpointId])) {
            return eventuallyResolveDesignTokens(valuesByBreakpoint[fallbackBreakpointId]);
        }
    }
    else {
        // Old design properties did not support breakpoints, keep for backward compatibility
        return valuesByBreakpoint;
    }
};
/** Overwrites the default value breakpoint by breakpoint. If a breakpoint
 * is not overwritten, it will fall back to the default. */
function mergeDesignValuesByBreakpoint(defaultValue, overwriteValue) {
    if (!defaultValue || !overwriteValue) {
        return defaultValue ?? overwriteValue;
    }
    const mergedValuesByBreakpoint = { ...defaultValue.valuesByBreakpoint };
    for (const [breakpointId, value] of Object.entries(overwriteValue.valuesByBreakpoint)) {
        if (!isValidBreakpointValue(value)) {
            continue;
        }
        mergedValuesByBreakpoint[breakpointId] = value;
    }
    return {
        type: 'DesignValue',
        valuesByBreakpoint: mergedValuesByBreakpoint,
    };
}
const BREAKPOINTS_STRATEGY_DESKTOP_FIRST = 'desktop-first';
const BREAKPOINTS_STRATEGY_MOBILE_FIRST = 'mobile-first';
/**
 * Detects the breakpoint strategy based on the provided breakpoints.
 *
 * @param breakpoints The array of breakpoints to analyze.
 * @returns The detected breakpoint strategy or undefined if not determinable.
 */
const detectBreakpointsStrategy = (breakpoints) => {
    if (breakpoints.length < 2) {
        return undefined;
    }
    const hasMobileFirst = breakpoints.slice(1).every((bp) => bp.query.startsWith('>'));
    if (hasMobileFirst) {
        return BREAKPOINTS_STRATEGY_MOBILE_FIRST;
    }
    const hasDesktopFirst = breakpoints.slice(1).every((bp) => bp.query.startsWith('<'));
    if (hasDesktopFirst) {
        return BREAKPOINTS_STRATEGY_DESKTOP_FIRST;
    }
    return undefined;
};

const CF_DEBUG_KEY = 'cf_debug';
/**
 * To ensure that the localStorage API can be used safely, we check
 * for availability (e.g. undefined in Node.js). Additionally, we
 * check if the localStorage can be used as some browsers throw a
 * SecurityError (e.g. Brave or Chromium with specific settings).
 */
const checkLocalStorageAvailability = () => {
    try {
        // Even the typeof check can throw an error in an agressive browser like Brave (requires using the deprecated flag #block-all-cookies-toggle)
        if (typeof localStorage === 'undefined' || localStorage === null) {
            return false;
        }
        // Attempt to set and remove an item to check if localStorage is enabled
        const TEST_KEY = 'cf_test_local_storage';
        localStorage.setItem(TEST_KEY, 'yes');
        if (localStorage.getItem(TEST_KEY) === 'yes') {
            localStorage.removeItem(TEST_KEY);
            return true;
        }
        else {
            return false;
        }
    }
    catch (_error) {
        return false;
    }
};
const DEBUG_LEVELS_HIERARCHY = ['error', 'warn', 'log', 'debug'];
class DebugLogger {
    constructor() {
        this.activeLevel = 'warn';
        // Public methods for logging
        this.error = this.logger('error');
        this.warn = this.logger('warn');
        this.log = this.logger('log');
        this.debug = this.logger('debug');
        if (!checkLocalStorageAvailability()) {
            return;
        }
        // Default to checking localStorage for the debug mode on initialization if in browser
        if (localStorage.getItem(CF_DEBUG_KEY) === 'true') {
            this.activeLevel = 'debug';
        }
    }
    static getInstance() {
        if (this.instance === null) {
            this.instance = new DebugLogger();
        }
        return this.instance;
    }
    getActiveLevel() {
        return this.activeLevel;
    }
    setActiveLevel(level) {
        this.activeLevel = level;
        if (!checkLocalStorageAvailability()) {
            return;
        }
        if (this.activeLevel === 'debug' || this.activeLevel === 'log') {
            localStorage.setItem(CF_DEBUG_KEY, 'true');
        }
        else {
            localStorage.removeItem(CF_DEBUG_KEY);
        }
    }
    // Log method for different levels (error, warn, log)
    logger(level) {
        return (...args) => {
            const levelPriority = DEBUG_LEVELS_HIERARCHY.indexOf(level);
            const activeLevelPriority = DEBUG_LEVELS_HIERARCHY.indexOf(this.activeLevel);
            const enabled = levelPriority <= activeLevelPriority;
            if (enabled) {
                console[level](...args);
            }
        };
    }
}
DebugLogger.instance = null;
const debug = DebugLogger.getInstance();
/** Set the logging level to `debug` */
const enableDebug = () => {
    debug.setActiveLevel('debug');
    console.log('Debug mode enabled');
};
/** Set the debug level to `warn` */
const disableDebug = () => {
    debug.setActiveLevel('warn');
    console.log('Debug mode disabled');
};
/** Set the debug level to the provided level */
const setDebugLevel = (level) => {
    debug.setActiveLevel(level);
    console.log(`Debug mode set to ${level}`);
};

const findOutermostCoordinates = (first, second) => {
    return {
        top: Math.min(first.top, second.top),
        right: Math.max(first.right, second.right),
        bottom: Math.max(first.bottom, second.bottom),
        left: Math.min(first.left, second.left),
    };
};
const isElementHidden = (rect) => {
    /** if the rect has no size and position, its element is not rendered in the DOM */
    return rect.width === 0 && rect.height === 0 && rect.x === 0 && rect.y === 0;
};
const getElementCoordinates = (element) => {
    const rect = element.getBoundingClientRect();
    /**
     * If element does not have children, or element has it's own width or height,
     * return the element's coordinates.
     */
    if (element.children.length === 0 || rect.width !== 0 || rect.height !== 0) {
        return rect;
    }
    const rects = [];
    /**
     * If element has children, or element does not have it's own width and height,
     * we find the cordinates of the children, and assume the outermost coordinates of the children
     * as the coordinate of the element.
     *
     * E.g child1 => {top: 2, bottom: 3, left: 4, right: 6} & child2 => {top: 1, bottom: 8, left: 12, right: 24}
     * The final assumed coordinates of the element would be => { top: 1, right: 24, bottom: 8, left: 4 }
     */
    for (const child of element.children) {
        const childRect = getElementCoordinates(child);
        if (childRect.width !== 0 || childRect.height !== 0) {
            const { top, right, bottom, left } = childRect;
            rects.push({ top, right, bottom, left });
        }
    }
    if (rects.length === 0) {
        return rect;
    }
    const { top, right, bottom, left } = rects.reduce(findOutermostCoordinates);
    return DOMRect.fromRect({
        x: left,
        y: top,
        height: bottom - top,
        width: right - left,
    });
};

const isLinkToAsset = (variable) => {
    if (variable === null || typeof variable !== 'object')
        return false;
    // The `'prop' in` pattern is informing TypeScript of the object shape, no need to cast `as`.
    if (!('sys' in variable))
        return false;
    if (variable.sys === null || typeof variable.sys !== 'object')
        return false;
    if (!('linkType' in variable.sys))
        return false;
    if (!('id' in variable.sys))
        return false;
    if (!('type' in variable.sys))
        return false;
    return (variable.sys?.linkType === 'Asset' &&
        typeof variable.sys?.id === 'string' &&
        !!variable.sys?.id &&
        variable.sys?.type === 'Link');
};

const isLinkToEntry = (variable) => {
    if (variable === null || typeof variable !== 'object')
        return false;
    // The `'prop' in` pattern is informing TypeScript of the object shape, no need to cast `as`.
    if (!('sys' in variable))
        return false;
    if (variable.sys === null || typeof variable.sys !== 'object')
        return false;
    if (!('linkType' in variable.sys))
        return false;
    if (!('id' in variable.sys))
        return false;
    if (!('type' in variable.sys))
        return false;
    return (variable.sys?.linkType === 'Entry' &&
        typeof variable.sys?.id === 'string' &&
        !!variable.sys?.id &&
        variable.sys?.type === 'Link');
};

function isLink(maybeLink) {
    if (maybeLink === null)
        return false;
    if (typeof maybeLink !== 'object')
        return false;
    const link = maybeLink;
    return Boolean(link.sys?.id) && link.sys?.type === 'Link' && Boolean(link.sys?.linkType);
}

/**
 * Localizes the provided entry or asset to match the regular format of CDA/CPA entities.
 * Note that this function does not apply a fallback to the default locale nor does it check
 * the content type for the localization setting of each field.
 * It will simply resolve each field to the requested locale. As using single and multiple
 * reference fields is still considered an experimental feature, this function does not handle
 * recursive localization of deeper referenced entities.
 *
 * If the entity is already localized, it will return the entity as is.
 *
 * Note that localization is later on determined by the existence of the `sys.locale` property (matching the API shape).
 *
 * @example
 * ```
 * const multiLocaleEntry = { fields: { title: { 'en-US': 'Hello' } } };
 * const localizedEntry = localizeEntity(multiLocaleEntry, 'en-US');
 * console.log(localizedEntry.fields.title); // 'Hello'
 * ```
 */
function localizeEntity(entity, locale) {
    if (!entity || !entity.fields) {
        throw new Error('Invalid entity provided');
    }
    if (entity.sys.locale) {
        return entity;
    }
    const cloned = cloneDeep(entity);
    // Set the requested locale as entry locale to follow the API shape:
    // https://www.contentful.com/developers/docs/references/content-delivery-api/#/introduction/common-resource-attributes
    cloned.sys.locale = locale;
    for (const key in cloned.fields) {
        cloned.fields[key] = cloned.fields[key][locale];
    }
    return cloned;
}

/**
 * This module encapsulates format of the path to a deep reference.
 */
const isPreboundProp = (variable) => {
    return (variable.type === 'BoundValue' &&
        typeof variable.isPrebound === 'boolean' &&
        !!variable.pathsByContentType);
};
const parseDataSourcePathIntoFieldset = (path) => {
    const parsedPath = parseDeepPath(path);
    if (null === parsedPath) {
        throw new Error(`Cannot parse path '${path}' as deep path`);
    }
    return parsedPath.fields.map((field) => [null, field, '~locale']);
};
/**
 * Parse path into components, supports  L1 references (one reference follow) atm.
 * @param path from data source. eg. `/uuid123/fields/image/~locale/fields/file/~locale`
 *                               eg. `/uuid123/fields/file/~locale/fields/title/~locale`
 * @returns
 */
const parseDataSourcePathWithL1DeepBindings = (path) => {
    const parsedPath = parseDeepPath(path);
    if (null === parsedPath) {
        throw new Error(`Cannot parse path '${path}' as deep path`);
    }
    return {
        key: parsedPath.key,
        field: parsedPath.fields[0],
        referentField: parsedPath.fields[1],
    };
};
/**
 * Detects if paths is valid deep-path, like:
 *  - /gV6yKXp61hfYrR7rEyKxY/fields/mainStory/~locale/fields/cover/~locale/fields/title/~locale
 * or regular, like:
 *  - /6J8eA60yXwdm5eyUh9fX6/fields/mainStory/~locale
 * @returns
 */
const isDeepPath = (deepPathCandidate) => {
    const deepPathParsed = parseDeepPath(deepPathCandidate);
    if (!deepPathParsed) {
        return false;
    }
    return deepPathParsed.fields.length > 1;
};
const isDeepPrebinding = (boundValueProperty) => {
    if (!boundValueProperty?.path || boundValueProperty.type !== 'BoundValue') {
        return false;
    }
    if (!isPreboundProp(boundValueProperty)) {
        return false;
    }
    if (isDeepPath(boundValueProperty.path)) {
        return true;
    }
    const hasDeepPathByContentType = Object.values(boundValueProperty.pathsByContentType || {}).some((val) => isDeepPath(val.path));
    return hasDeepPathByContentType;
};
const getPrebindingPathBySourceEntry = (preboundValueProperty, getHeadEntityByDataSourceKey) => {
    if (!isPreboundProp(preboundValueProperty)) {
        return undefined;
    }
    // incomplete path due to several content types and not known default source
    const [, dataSourceKey] = preboundValueProperty.path.split('/');
    if (!dataSourceKey) {
        return undefined;
    }
    const headEntity = getHeadEntityByDataSourceKey(dataSourceKey);
    if (headEntity?.sys.type !== 'Entry') {
        return undefined;
    }
    const contentTypeId = headEntity.sys.contentType.sys.id;
    return preboundValueProperty.pathsByContentType?.[contentTypeId]?.path;
};
const parseDeepPath = (deepPathCandidate) => {
    // ALGORITHM:
    // We start with deep path in form:
    // /uuid123/fields/mainStory/~locale/fields/cover/~locale/fields/title/~locale
    // First turn string into array of segments
    //      ['', 'uuid123', 'fields', 'mainStory', '~locale', 'fields', 'cover', '~locale', 'fields', 'title', '~locale']
    // Then group segments into intermediate represenatation - chunks, where each non-initial chunk starts with 'fields'
    //      [
    //        [ "", "uuid123" ],
    //        [ "fields", "mainStory", "~locale" ],
    //        [ "fields", "cover", "~locale" ],
    //        [ "fields", "title", "~locale" ]
    //      ]
    // Then check "initial" chunk for corretness
    // Then check all "field-leading" chunks for correctness
    const isValidInitialChunk = (initialChunk) => {
        // must have start with '' and have at least 2 segments, second non-empty
        // eg. /-_432uuid123123
        return /^\/([^/^~]+)$/.test(initialChunk.join('/'));
    };
    const isValidFieldChunk = (fieldChunk) => {
        // must start with 'fields' and have at least 3 segments, second non-empty and last segment must be '~locale'
        // eg. fields/-32234mainStory/~locale
        return /^fields\/[^/^~]+\/~locale$/.test(fieldChunk.join('/'));
    };
    const deepPathSegments = deepPathCandidate.split('/');
    const chunks = chunkSegments(deepPathSegments, { startNextChunkOnElementEqualTo: 'fields' });
    if (chunks.length <= 1) {
        return null; // malformed path, even regular paths have at least 2 chunks
    }
    else if (chunks.length === 2) {
        return null; // deep paths have at least 3 chunks
    }
    // With 3+ chunks we can now check for deep path correctness
    const [initialChunk, ...fieldChunks] = chunks;
    if (!isValidInitialChunk(initialChunk)) {
        return null;
    }
    if (!fieldChunks.every(isValidFieldChunk)) {
        return null;
    }
    return {
        key: initialChunk[1], // pick uuid from initial chunk ['','uuid123'],
        fields: fieldChunks.map((fieldChunk) => fieldChunk[1]), // pick  only fieldName eg. from  ['fields','mainStory', '~locale'] we pick `mainStory`
    };
};
const chunkSegments = (segments, { startNextChunkOnElementEqualTo }) => {
    const chunks = [];
    let currentChunk = [];
    const isSegmentBeginningOfChunk = (segment) => segment === startNextChunkOnElementEqualTo;
    const excludeEmptyChunks = (chunk) => chunk.length > 0;
    for (let i = 0; i < segments.length; i++) {
        const isInitialElement = i === 0;
        const segment = segments[i];
        if (isInitialElement) {
            currentChunk = [segment];
        }
        else if (isSegmentBeginningOfChunk(segment)) {
            chunks.push(currentChunk);
            currentChunk = [segment];
        }
        else {
            currentChunk.push(segment);
        }
    }
    chunks.push(currentChunk);
    return chunks.filter(excludeEmptyChunks);
};
const lastPathNamedSegmentEq = (path, expectedName) => {
    // `/key123/fields/featureImage/~locale/fields/file/~locale`
    // ['', 'key123', 'fields', 'featureImage', '~locale', 'fields', 'file', '~locale']
    const segments = path.split('/');
    if (segments.length < 2) {
        debug.warn(`[experiences-core::pathSchema] Attempting to check whether last named segment of the path (${path}) equals to '${expectedName}', but the path doesn't have enough segments.`);
        return false;
    }
    const secondLast = segments[segments.length - 2]; // skipping trailing '~locale'
    return secondLast === expectedName;
};

const resolveHyperlinkPattern = (pattern, entry, locale) => {
    if (!entry || !locale)
        return null;
    const variables = {
        entry,
        locale,
    };
    return buildTemplate({ template: pattern, context: variables });
};
function getValue(obj, path) {
    return path
        .replace(/\[/g, '.')
        .replace(/\]/g, '')
        .split('.')
        .reduce((o, k) => (o || {})[k], obj);
}
function addLocale(str, locale) {
    const fieldsIndicator = 'fields';
    const fieldsIndex = str.indexOf(fieldsIndicator);
    if (fieldsIndex !== -1) {
        const dotIndex = str.indexOf('.', fieldsIndex + fieldsIndicator.length + 1); // +1 for '.'
        if (dotIndex !== -1) {
            return str.slice(0, dotIndex + 1) + locale + '.' + str.slice(dotIndex + 1);
        }
    }
    return str;
}
function getTemplateValue(ctx, path) {
    const pathWithLocale = addLocale(path, ctx.locale);
    const retrievedValue = getValue(ctx, pathWithLocale);
    return typeof retrievedValue === 'object' && retrievedValue !== null
        ? retrievedValue[ctx.locale]
        : retrievedValue;
}
function buildTemplate({ template, context, }) {
    const localeVariable = /{\s*locale\s*}/g;
    // e.g. "{ page.sys.id }"
    const variables = /{\s*([\S]+?)\s*}/g;
    return (template
        // first replace the locale pattern
        .replace(localeVariable, context.locale)
        // then resolve the remaining variables
        .replace(variables, (_, path) => {
        const fallback = path + '_NOT_FOUND';
        const value = getTemplateValue(context, path) ?? fallback;
        // using _.result didn't gave proper results so we run our own version of it
        return String(typeof value === 'function' ? value() : value);
    }));
}

const stylesToKeep = ['cfImageAsset'];
const stylesToRemove = CF_STYLE_ATTRIBUTES.filter((style) => !stylesToKeep.includes(style));
// cfWrapColumns & cfWrapColumnsCount are no real style attributes as they are handled on the editor side
const propsToRemove = ['cfSsrClassName', 'cfWrapColumns', 'cfWrapColumnsCount'];
const sanitizeNodeProps = (nodeProps) => {
    return omit(nodeProps, stylesToRemove, propsToRemove);
};

/** Turn the visibility value into a style object that can be used for inline styles in React */
const transformVisibility = (value) => {
    if (value === false) {
        return {
            display: 'none !important',
        };
    }
    // Don't explicitly set anything when visible to not overwrite values like `grid` or `flex`.
    return {};
};
const transformGridColumn = (span) => {
    if (!span) {
        return {};
    }
    return {
        gridColumn: `span ${span}`,
    };
};
const transformBorderStyle = (value) => {
    if (!value)
        return {};
    const parts = value.split(' ');
    // Just accept the passed value
    if (parts.length < 3)
        return { border: value };
    const [borderSize, borderStyle, ...borderColorParts] = parts;
    const borderColor = borderColorParts.join(' ');
    return {
        border: `${borderSize} ${borderStyle} ${borderColor}`,
    };
};
const transformAlignment = (cfHorizontalAlignment, cfVerticalAlignment, cfFlexDirection = 'column') => cfFlexDirection === 'row'
    ? {
        alignItems: cfHorizontalAlignment,
        justifyContent: cfVerticalAlignment === 'center' ? `safe ${cfVerticalAlignment}` : cfVerticalAlignment,
    }
    : {
        alignItems: cfVerticalAlignment,
        justifyContent: cfHorizontalAlignment === 'center'
            ? `safe ${cfHorizontalAlignment}`
            : cfHorizontalAlignment,
    };
const transformBackgroundImage = (cfBackgroundImageUrl, cfBackgroundImageOptions) => {
    const matchBackgroundSize = (scaling) => {
        if ('fill' === scaling)
            return 'cover';
        if ('fit' === scaling)
            return 'contain';
    };
    const matchBackgroundPosition = (alignment) => {
        if (!alignment || 'string' !== typeof alignment) {
            return;
        }
        let [horizontalAlignment, verticalAlignment] = alignment.trim().split(/\s+/, 2);
        const isHorizontalValid = ['left', 'right', 'center'].includes(horizontalAlignment);
        const isVerticalValid = ['top', 'bottom', 'center'].includes(verticalAlignment);
        horizontalAlignment = isHorizontalValid ? horizontalAlignment : 'left';
        verticalAlignment = isVerticalValid ? verticalAlignment : 'top';
        return `${horizontalAlignment} ${verticalAlignment}`;
    };
    if (!cfBackgroundImageUrl) {
        return;
    }
    let backgroundImage;
    let backgroundImageSet;
    if (typeof cfBackgroundImageUrl === 'string') {
        backgroundImage = `url(${cfBackgroundImageUrl})`;
    }
    else {
        const imgSet = cfBackgroundImageUrl.srcSet?.join(',');
        backgroundImage = `url(${cfBackgroundImageUrl.url})`;
        backgroundImageSet = `image-set(${imgSet})`;
    }
    return {
        backgroundImage,
        backgroundImage2: backgroundImageSet,
        backgroundRepeat: cfBackgroundImageOptions?.scaling === 'tile' ? 'repeat' : 'no-repeat',
        backgroundPosition: matchBackgroundPosition(cfBackgroundImageOptions?.alignment),
        backgroundSize: matchBackgroundSize(cfBackgroundImageOptions?.scaling),
    };
};
const transformTextAlign = (value) => {
    if (!value)
        return undefined;
    const sdkOptions = getSdkOptions();
    // New behavior: translate left/right to start/end
    // Customer can opt-out by activating this global option toggle
    if (!sdkOptions.__disableTextAlignmentTransform) {
        if (value === 'left')
            return 'start';
        if (value === 'right')
            return 'end';
    }
    return value;
};

const toCSSAttribute = (key) => {
    let val = key.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
    // Remove the number from the end of the key to allow for overrides on style properties
    val = val.replace(/\d+$/, '');
    return val;
};
/**
 * Turns a list of CSSProperties into a joined CSS string that can be
 * used for <style> tags. Per default it creates a minimized version.
 * For editor mode, use the `useWhitespaces` flag to create a more readable version.
 *
 * @param cssProperties list of CSS properties
 * @param useWhitespaces adds whitespaces and newlines between each rule
 * @returns a string of CSS rules
 */
const stringifyCssProperties = (cssProperties, useWhitespaces = false) => {
    const rules = Object.entries(cssProperties)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => useWhitespaces ? `${toCSSAttribute(key)}: ${value};` : `${toCSSAttribute(key)}:${value};`);
    return rules.join(useWhitespaces ? '\n' : '');
};
const buildStyleTag = ({ styles, nodeId }) => {
    const generatedStyles = stringifyCssProperties(styles, true);
    const className = `cfstyles-${nodeId ? nodeId : md5(generatedStyles)}`;
    const styleRule = `.${className}{ ${generatedStyles} }`;
    return [className, styleRule];
};
/**
 * Takes plain design values and transforms them into CSS properties. Undefined values will
 * be filtered out.
 *
 * **Example Input**
 * ```
 * values = {
 *   cfVisibility: 'visible',
 *   cfMargin: '10px',
 *   cfFlexReverse: true,
 *   cfImageOptions: { objectFit: 'cover' },
 *   // ...
 * }
 * ```
 * **Example Output**
 * ```
 * cssProperties = {
 *   margin: '10px',
 *   flexDirection: 'row-reverse',
 *   objectFit: 'cover',
 *   // ...
 * }
 * ```
 */
const buildCfStyles = (values) => {
    const cssProperties = {
        margin: values.cfMargin,
        padding: values.cfPadding,
        backgroundColor: values.cfBackgroundColor,
        width: values.cfWidth || values.cfImageOptions?.width,
        height: values.cfHeight || values.cfImageOptions?.height,
        maxWidth: values.cfMaxWidth,
        ...transformGridColumn(values.cfColumnSpan),
        ...transformBorderStyle(values.cfBorder),
        borderRadius: values.cfBorderRadius,
        gap: values.cfGap,
        ...transformAlignment(values.cfHorizontalAlignment, values.cfVerticalAlignment, values.cfFlexDirection),
        flexDirection: values.cfFlexReverse && values.cfFlexDirection
            ? `${values.cfFlexDirection}-reverse`
            : values.cfFlexDirection,
        flexWrap: values.cfFlexWrap,
        ...transformBackgroundImage(values.cfBackgroundImageUrl, values.cfBackgroundImageOptions),
        fontSize: values.cfFontSize,
        fontWeight: values.cfTextBold ? 'bold' : values.cfFontWeight,
        fontStyle: values.cfTextItalic ? 'italic' : undefined,
        textDecoration: values.cfTextUnderline ? 'underline' : undefined,
        lineHeight: values.cfLineHeight,
        letterSpacing: values.cfLetterSpacing,
        color: values.cfTextColor,
        textAlign: transformTextAlign(values.cfTextAlign),
        textTransform: values.cfTextTransform,
        objectFit: values.cfImageOptions?.objectFit,
        objectPosition: values.cfImageOptions?.objectPosition,
    };
    const cssPropertiesWithoutUndefined = Object.fromEntries(Object.entries(cssProperties).filter(([, value]) => value !== undefined));
    return cssPropertiesWithoutUndefined;
};
/**
 * **Only meant to be used in editor mode!**
 *
 * If the node is an empty structure component with a relative height (e.g. '100%'),
 * it might render with a zero height. In this case, add a min height of 80px to ensure
 * that child nodes can be added via drag & drop.
 */
const addMinHeightForEmptyStructures = (cssProperties, node) => {
    if (!node.children.length &&
        isStructureWithRelativeHeight(node.definitionId, cssProperties.height)) {
        return {
            ...cssProperties,
            minHeight: EMPTY_CONTAINER_SIZE,
        };
    }
    return cssProperties;
};
/**
 * Container/section default behavior:
 * Default height => height: EMPTY_CONTAINER_SIZE
 * If a container component has children => height: 'fit-content'
 */
const calculateNodeDefaultHeight = ({ blockId, children, value, }) => {
    if (!blockId || !isContentfulStructureComponent(blockId) || value !== 'auto') {
        return value;
    }
    if (children.length) {
        return '100%';
    }
    return EMPTY_CONTAINER_SIZE;
};

function getOptimizedImageUrl(url, width, quality, format) {
    if (url.startsWith('//')) {
        url = 'https:' + url;
    }
    const params = new URLSearchParams();
    if (width) {
        params.append('w', width.toString());
    }
    if (quality && quality > 0 && quality < 100) {
        params.append('q', quality.toString());
    }
    if (format) {
        params.append('fm', format);
    }
    const queryString = params.toString();
    return `${url}${queryString ? '?' + queryString : ''}`;
}

function validateParams(file, quality, format) {
    if (!file.details.image) {
        throw Error('No image in file asset to transform');
    }
    if (quality < 0 || quality > 100) {
        throw Error('Quality must be between 0 and 100');
    }
    if (format && !SUPPORTED_IMAGE_FORMATS.includes(format)) {
        throw Error(`Format must be one of ${SUPPORTED_IMAGE_FORMATS.join(', ')}`);
    }
    return true;
}

const MAX_WIDTH_ALLOWED$1 = 2000;
const getOptimizedBackgroundImageAsset = (file, widthStyle, quality = '100%', format) => {
    const qualityNumber = Number(quality.replace('%', ''));
    if (!validateParams(file, qualityNumber, format)) ;
    if (!validateParams(file, qualityNumber, format)) ;
    const url = file.url;
    const { width1x, width2x } = getWidths(widthStyle, file);
    const imageUrl1x = getOptimizedImageUrl(url, width1x, qualityNumber, format);
    const imageUrl2x = getOptimizedImageUrl(url, width2x, qualityNumber, format);
    const srcSet = [`url(${imageUrl1x}) 1x`, `url(${imageUrl2x}) 2x`];
    const returnedUrl = getOptimizedImageUrl(url, width2x, qualityNumber, format);
    const optimizedBackgroundImageAsset = {
        url: returnedUrl,
        srcSet,
        file,
    };
    return optimizedBackgroundImageAsset;
    function getWidths(widthStyle, file) {
        let width1x = 0;
        let width2x = 0;
        const intrinsicImageWidth = file.details.image.width;
        if (widthStyle.endsWith('px')) {
            width1x = Math.min(Number(widthStyle.replace('px', '')), intrinsicImageWidth);
        }
        else {
            width1x = Math.min(MAX_WIDTH_ALLOWED$1, intrinsicImageWidth);
        }
        width2x = Math.min(width1x * 2, intrinsicImageWidth);
        return { width1x, width2x };
    }
};

const detachExperienceStyles = (experience) => {
    const experienceTreeRoot = experience.entityStore?.experienceEntryFields
        ?.componentTree;
    if (!experienceTreeRoot) {
        return;
    }
    const mapOfDesignVariableKeys = flattenDesignTokenRegistry(designTokensRegistry);
    // getting breakpoints from the entry componentTree field
    const { breakpoints } = experienceTreeRoot;
    // creating the structure which I thought would work best for aggregation
    const mediaQueryDataByBreakpoint = breakpoints.reduce((mediaQueryTemplate, breakpoint) => ({
        ...mediaQueryTemplate,
        [breakpoint.id]: {
            condition: breakpoint.query,
            cssByClassName: {},
            visibilityCssByClassName: {},
        },
    }), {});
    // getting the breakpoint ids
    const breakpointIds = Object.keys(mediaQueryDataByBreakpoint);
    const iterateOverTreeAndExtractStyles = ({ componentTree, dataSource, unboundValues, componentSettings, componentVariablesOverwrites, patternWrapper, wrappingPatternIds, wrappingPatternNodeIds = [], }) => {
        // traversing the tree
        const queue = [];
        queue.push(...componentTree.children);
        // for each tree node
        while (queue.length) {
            const currentNode = queue.shift();
            if (!currentNode) {
                break;
            }
            const usedComponents = experience.entityStore?.usedComponents ?? [];
            const isPatternNode = checkIsAssemblyNode({
                componentId: currentNode.definitionId,
                usedComponents,
            });
            if (isPatternNode) {
                // When detecting a circular dependency among patterns, stop to avoid an infinite loop
                if (wrappingPatternIds.has(currentNode.definitionId)) {
                    continue;
                }
                const patternEntry = usedComponents.find((component) => component.sys.id === currentNode.definitionId);
                if (!patternEntry || !('fields' in patternEntry)) {
                    continue;
                }
                const nextComponentVariablesOverwrites = resolveComponentVariablesOverwrites({
                    patternNode: currentNode,
                    wrapperComponentVariablesOverwrites: componentVariablesOverwrites,
                    wrapperComponentSettings: componentSettings,
                });
                // the node of a used pattern contains only the definitionId (id of the patter entry)
                // as well as the variables overwrites
                // the layout of a pattern is stored in it's entry
                iterateOverTreeAndExtractStyles({
                    // that is why we pass it here to iterate of the pattern tree
                    componentTree: patternEntry.fields.componentTree,
                    // but we pass the data source of the experience entry cause that's where the binding is stored
                    dataSource,
                    // unbound values of a pattern store the default values of pattern variables
                    unboundValues: patternEntry.fields.unboundValues,
                    // this is where we can map the pattern variable to it's default value
                    componentSettings: patternEntry.fields.componentSettings,
                    // and this is where the over-writes for the default values are stored
                    // yes, I know, it's a bit confusing
                    componentVariablesOverwrites: nextComponentVariablesOverwrites,
                    // pass top-level pattern node to store instance-specific child styles for rendering
                    patternWrapper: currentNode,
                    wrappingPatternIds: new Set([...wrappingPatternIds, currentNode.definitionId]),
                    wrappingPatternNodeIds: [...wrappingPatternNodeIds, currentNode.id || ''],
                });
                continue;
            }
            /* [Data format] `currentNode.variables` uses the following serialized shape:
             * {
             *   cfMargin: { type: 'DesignValue', valuesByBreakpoint: { desktop: '1px', tablet: '2px' } },
             *   cfPadding: { type: 'DesignValue', valuesByBreakpoint: { desktop: '3px' } }
             *   cfBackgroundImageUrl: { type: 'BoundValue', path: '/lUERH7tX7nJTaPX6f0udB/fields/assetReference/~locale/fields/file/~locale' }
             *   asdf1234: { type: 'ComponentValue', key: 'qwer567' }
             *   // ...
             * }
             */
            // so first, I convert it into a map to help me make it easier to access the values
            const propsByBreakpoint = indexByBreakpoint({
                variables: currentNode.variables,
                breakpointIds,
                unboundValues: unboundValues,
                dataSource: dataSource,
                componentSettings,
                componentVariablesOverwrites,
                getBoundEntityById: (id) => {
                    return experience.entityStore?.entities.find((entity) => entity.sys.id === id);
                },
            });
            // When the node is hidden for any breakpoint, we need to handle this separately with a disjunct media query.
            const isAnyVisibilityValueHidden = Object.values(propsByBreakpoint).some((designProperties) => designProperties.cfVisibility === false);
            // We always need an explicit value when using disjunct media queries
            // Example: desktop uses "false" and tablet is undefined -> we need to set `display: none` for tablet as well.
            let previousVisibilityValue = undefined;
            /* [Data format] `propsByBreakpoint` is a map of "breakpointId > propertyName > plainValue":
             * {
             *   desktop: {
             *     cfMargin: '1px',
             *     cfWidth: '100%',
             *     cfBackgroundImageUrl: 'https://example.com/image.jpg'
             *     //...
             *   }
             * }
             */
            const currentNodeClassNames = [];
            // Chain IDs to avoid overwriting styles across multiple instances of the same pattern
            // e.g. `{outerPatternNodeId}{innerPatternNodeId}-{currentNodeId}`
            // (!) Notice that the chain of patterns (before the dash) follows the format of prebinding/ parameters
            const currentNodeIdsChain = `${wrappingPatternNodeIds.join('')}-${currentNode.id}`;
            // For each breakpoint, resolve design tokens, create the CSS and generate a unique className.
            for (const breakpointId of breakpointIds) {
                const propsByBreakpointWithResolvedDesignTokens = Object.entries(propsByBreakpoint[breakpointId]).reduce((acc, [variableName, variableValue]) => {
                    return {
                        ...acc,
                        [variableName]: maybePopulateDesignTokenValue(variableName, variableValue, mapOfDesignVariableKeys),
                    };
                }, {});
                // Convert CF-specific property names to CSS variables, e.g. `cfMargin` -> `margin`
                const cfStyles = buildCfStyles(propsByBreakpointWithResolvedDesignTokens);
                /* [Data format] `cfStyles` is a list of CSSProperties (React format):
                 * {
                 *   margin: '1px',
                 *   width: '100%',
                 *   backgroundImage: 'url(https://example.com/image.jpg)'
                 *   //...
                 * }
                 */
                const generatedCss = stringifyCssProperties(cfStyles);
                if (!generatedCss && !isAnyVisibilityValueHidden) {
                    // If there are no styles to apply, skip this breakpoint completely including the class name
                    continue;
                }
                /* [Data format] `generatedCss` is the minimized CSS string that will be added to the DOM:
                 * generatedCss = "margin: 1px;width: 100%;..."
                 */
                // - Adding breakpointId to ensure not using the same IDs between breakpoints as this leads to
                // conflicts between different breakpoint values from multiple nodes where the hash would be equal
                // - Adding wrapping pattern nodes IDs to avoid conflicts between similar nested patterns as those
                // could override each others CSS for some breakpoints just through the order of `<style>` tags in the DOM.
                const styleHash = md5(currentNodeIdsChain + breakpointId + generatedCss);
                // and prefix the className to make sure the value can be processed
                const className = `cf-${styleHash}`;
                // I save the generated hashes into an array to later save it in the tree node
                // as cfSsrClassName prop
                // making sure to avoid the duplicates in case styles for > 1 breakpoints are the same
                if (!currentNodeClassNames.includes(className)) {
                    currentNodeClassNames.push(className);
                }
                // Only if the hash was not used yet, save the CSS to the stylesheet
                if (!mediaQueryDataByBreakpoint[breakpointId].cssByClassName[className]) {
                    mediaQueryDataByBreakpoint[breakpointId].cssByClassName[className] = generatedCss;
                }
                // Special case for visibility to override any custom `display` values but only for a specific breakpoint.
                if (isAnyVisibilityValueHidden) {
                    const visibilityValue = propsByBreakpointWithResolvedDesignTokens.cfVisibility ?? previousVisibilityValue;
                    previousVisibilityValue = visibilityValue;
                    const visibilityStyles = transformVisibility(visibilityValue);
                    const visibilityCss = stringifyCssProperties(visibilityStyles);
                    if (!mediaQueryDataByBreakpoint[breakpointId].visibilityCssByClassName[className]) {
                        mediaQueryDataByBreakpoint[breakpointId].visibilityCssByClassName[className] =
                            visibilityCss;
                    }
                }
            }
            // all generated classNames are saved in the tree node
            // to be handled by the sdk later
            // each node will get N classNames, where N is the number of breakpoints
            // browsers process classNames in the order they are defined
            // meaning that in case of className1 className2 className3
            // className3 will win over className2 and className1
            // making sure that we respect the order of breakpoints from
            // we can achieve "desktop first" or "mobile first" approach to style over-writes
            if (patternWrapper) {
                // @ts-expect-error -- valueByBreakpoint is not explicitly defined, but it's already defined in the patternWrapper styles
                patternWrapper.variables.cfSsrClassName = {
                    ...(patternWrapper.variables.cfSsrClassName ?? {}),
                    type: 'DesignValue',
                    [currentNodeIdsChain]: {
                        valuesByBreakpoint: {
                            [breakpointIds[0]]: currentNodeClassNames.join(' '),
                        },
                    },
                };
            }
            else {
                currentNode.variables.cfSsrClassName = {
                    type: 'DesignValue',
                    valuesByBreakpoint: {
                        [breakpointIds[0]]: currentNodeClassNames.join(' '),
                    },
                };
            }
            queue.push(...currentNode.children);
        }
    };
    iterateOverTreeAndExtractStyles({
        componentTree: experienceTreeRoot,
        dataSource: experience.entityStore?.dataSource ?? {},
        unboundValues: experience.entityStore?.unboundValues ?? {},
        componentSettings: experience.entityStore?.experienceEntryFields?.componentSettings,
        wrappingPatternIds: new Set(experience.entityStore?.experienceEntryId ? [experience.entityStore.experienceEntryId] : []),
    });
    // once the whole tree was traversed, for each breakpoint, I aggregate the styles
    // for each generated className into one css string
    return Object.values(mediaQueryDataByBreakpoint).reduce((acc, { condition, cssByClassName, visibilityCssByClassName }, index) => {
        const mediaQueryCss = toMediaQuery({ cssByClassName, condition });
        // Handle visibility separately to use disjunct media queries ("if desktop but not tablet ...")
        // Enables to hide on one breakpoint but render any unknown custom `display` value on another breakpoint.
        const visibilityMediaQueryCss = toMediaQuery({
            cssByClassName: visibilityCssByClassName,
            condition,
            // Breakpoint validation ensures that it starts with the '*' breakpoint
            nextCondition: Object.values(mediaQueryDataByBreakpoint)[index + 1]?.condition,
        });
        return `${acc}${mediaQueryCss}${visibilityMediaQueryCss}`;
    }, '');
};
/**
 * Rendering pattern nodes inside pattern entry by injecting default values from the top:
 * When there is a ComponentValue but the recursive logic is not wrapped by a pattern node (no `componentVariablesOverwrites`),
 * a pattern entry is rendered directly. In this case, we replace each ComponentValue with the default value from componentSettings.
 */
const injectDefaultValuesForComponentValues = ({ patternNode, wrapperComponentSettings, }) => {
    const propertyDefinitions = wrapperComponentSettings?.variableDefinitions;
    return Object.entries(patternNode.variables).reduce((resolvedProperties, [propertyName, propertyValue]) => {
        if (propertyValue.type === 'ComponentValue') {
            const componentValueKey = propertyValue.key;
            const componentDefaultValue = propertyDefinitions?.[componentValueKey].defaultValue;
            // We're only considering design properties for styles generation
            if (componentDefaultValue?.type === 'DesignValue') {
                resolvedProperties[propertyName] = componentDefaultValue;
            }
        }
        else {
            // Do nothing when it's not a ComponentValue & just keep the defined value
            resolvedProperties[propertyName] = propertyValue;
        }
        return resolvedProperties;
    }, {});
};
/**
 * In case of nested patterns, we need to resolve the ComponentValue properties and overwrite them with the value
 * stored in the parent component.
 *
 *
 * @param patternNode - pattern node which contains the variables
 * @param componentVariablesOverwrites - object which contains the variables of the parent component
 */
const resolveComponentVariablesOverwrites = ({ patternNode, wrapperComponentVariablesOverwrites, wrapperComponentSettings, }) => {
    // In case of rendering a pattern entry, there are no custom ComponentValues.
    // So we pass down the default values from this pattern node down to each deeper pattern level.
    if (!wrapperComponentVariablesOverwrites) {
        return injectDefaultValuesForComponentValues({
            patternNode,
            wrapperComponentSettings,
        });
    }
    // Rendering (nested) pattern node inside another pattern node (for both experience & pattern entry):
    // The `wrapperComponentVariablesOverwrites` from the top-most pattern node is passed through to each child
    // node (and nested pattern nodes). It replaces each ComponentValue in the subtree with either the overwrite
    // or the default value.
    return Object.entries(patternNode?.variables).reduce((resolvedValues, [propertyName, propertyValue]) => {
        if (propertyValue.type === 'ComponentValue') {
            // Copying the values from the parent node
            const overwritingValue = wrapperComponentVariablesOverwrites?.[propertyValue.key];
            // Property definition from the parent pattern
            const propertyDefinition = wrapperComponentSettings?.variableDefinitions?.[propertyValue.key];
            const defaultValue = propertyDefinition?.defaultValue;
            // The overwriting value is either a custom value from the experience or default value from a
            // wrapping pattern node that got trickled down to this nesting level.
            resolvedValues[propertyName] = mergeDefaultAndOverwriteValues(defaultValue, overwritingValue);
        }
        else {
            // Keep raw values
            resolvedValues[propertyName] = propertyValue;
        }
        return resolvedValues;
    }, {});
};
const isCfStyleAttribute = (variableName) => {
    return CF_STYLE_ATTRIBUTES.includes(variableName);
};
const maybePopulateDesignTokenValue = (variableName, variableValue, mapOfDesignVariableKeys) => {
    // TODO: refactor to reuse fn from core package
    if (typeof variableValue !== 'string') {
        return variableValue;
    }
    if (!isCfStyleAttribute(variableName)) {
        return variableValue;
    }
    const resolveSimpleDesignToken = (variableName, variableValue) => {
        const nonTemplateDesignTokenValue = variableValue.replace(templateStringRegex, '$1');
        const tokenValue = mapOfDesignVariableKeys[nonTemplateDesignTokenValue];
        if (!tokenValue) {
            if (builtInStyles[variableName]) {
                return builtInStyles[variableName].defaultValue;
            }
            if (optionalBuiltInStyles[variableName]) {
                return optionalBuiltInStyles[variableName].defaultValue;
            }
            return '0px';
        }
        if (variableName === 'cfBorder' || variableName.startsWith('cfBorder_')) {
            if (typeof tokenValue === 'object') {
                const { width, style, color } = tokenValue;
                return `${width} ${style} ${color}`;
            }
        }
        return tokenValue;
    };
    const templateStringRegex = /\${(.+?)}/g;
    const parts = variableValue.split(' ');
    let resolvedValue = '';
    for (const part of parts) {
        const tokenValue = templateStringRegex.test(part)
            ? resolveSimpleDesignToken(variableName, part)
            : part;
        resolvedValue += `${tokenValue} `;
    }
    // Not trimming would end up with a trailing space that breaks the check in `calculateNodeDefaultHeight`
    return resolvedValue.trim();
};
const transformMedia$1 = (boundAsset, width, options) => {
    try {
        const asset = boundAsset;
        // Target width (px/rem/em) will be applied to the css url if it's lower than the original image width (in px)
        const assetDetails = asset.fields.file?.details;
        const assetWidth = assetDetails?.image?.width || 0; // This is always in px
        if (!options) {
            return asset.fields.file?.url;
        }
        const targetWidthObject = parseCSSValue(options.targetSize); // Contains value and unit (px/rem/em) so convert and then compare to assetWidth
        const targetValue = targetWidthObject ? getTargetValueInPixels(targetWidthObject) : assetWidth;
        if (targetValue < assetWidth)
            width = `${targetValue}px`;
        return getOptimizedBackgroundImageAsset(asset.fields.file, width, options.quality, options.format);
    }
    catch (error) {
        debug.error('[experiences-core::ssrStyles] Error transforming image asset', error);
    }
    return boundAsset.fields.file?.url;
};
const resolveBackgroundImageBinding = ({ variableData, getBoundEntityById, dataSource = {}, unboundValues = {}, componentVariablesOverwrites, componentSettings = { variableDefinitions: {} }, options, width, }) => {
    if (variableData.type === 'UnboundValue') {
        const uuid = variableData.key;
        return unboundValues[uuid]?.value;
    }
    if (variableData.type === 'ComponentValue') {
        const variableDefinitionKey = variableData.key;
        const variableDefinition = componentSettings.variableDefinitions[variableDefinitionKey];
        // @ts-expect-error TODO: Types coming from validations erroneously assume that `defaultValue` can be a primitive value (e.g. string or number)
        const defaultValueKey = variableDefinition.defaultValue?.key;
        const defaultValue = unboundValues[defaultValueKey].value;
        const overwriteValue = componentVariablesOverwrites?.[variableDefinitionKey];
        // overwriteValue is a ComponentValue we can safely return the default value
        if (!overwriteValue || overwriteValue.type === 'ComponentValue') {
            return defaultValue;
        }
        // at this point overwriteValue will either be type of 'DesignValue' or 'BoundValue'
        // so we recursively run resolution again to resolve it
        const resolvedValue = resolveBackgroundImageBinding({
            variableData: overwriteValue,
            getBoundEntityById,
            dataSource,
            unboundValues,
            componentVariablesOverwrites,
            componentSettings,
            options,
            width,
        });
        return resolvedValue || defaultValue;
    }
    if (variableData.type === 'BoundValue') {
        // '/lUERH7tX7nJTaPX6f0udB/fields/assetReference/~locale/fields/file/~locale'
        const [, uuid] = variableData.path.split('/');
        const binding = dataSource[uuid];
        const boundEntity = getBoundEntityById(binding.sys.id);
        if (!boundEntity) {
            return;
        }
        if (boundEntity.sys.type === 'Asset') {
            return transformMedia$1(boundEntity, width, options);
        }
        else {
            // '/lUERH7tX7nJTaPX6f0udB/fields/assetReference/~locale/fields/file/~locale'
            // becomes
            // '/fields/assetReference/~locale/fields/file/~locale'
            const pathWithoutUUID = variableData.path.split(uuid)[1];
            // '/fields/assetReference/~locale/fields/file/~locale'
            // becomes
            // '/fields/assetReference/'
            const pathToReferencedAsset = pathWithoutUUID.split('~locale')[0];
            // '/fields/assetReference/'
            // becomes
            // '[fields, assetReference]'
            const [, fieldName] = pathToReferencedAsset.substring(1).split('/') ?? undefined;
            const referenceToAsset = boundEntity.fields[fieldName];
            if (!referenceToAsset) {
                return;
            }
            if (referenceToAsset.sys?.linkType === 'Asset') {
                const referencedAsset = getBoundEntityById(referenceToAsset.sys.id);
                if (!referencedAsset) {
                    return;
                }
                return transformMedia$1(referencedAsset, width, options);
            }
        }
    }
};
const resolveVariable = ({ variableData, defaultBreakpoint, componentSettings = { variableDefinitions: {} }, componentVariablesOverwrites, }) => {
    if (variableData?.type === 'DesignValue') {
        return variableData.valuesByBreakpoint[defaultBreakpoint] || {};
    }
    else if (variableData?.type === 'ComponentValue') {
        const variableDefinitionKey = variableData.key;
        const variableDefinition = componentSettings.variableDefinitions[variableDefinitionKey];
        const defaultValue = variableDefinition.defaultValue;
        const userSetValue = componentVariablesOverwrites?.[variableDefinitionKey];
        if (!userSetValue || userSetValue.type === 'ComponentValue') {
            return defaultValue?.valuesByBreakpoint[defaultBreakpoint] || '';
        }
        return resolveVariable({
            variableData: userSetValue,
            defaultBreakpoint,
            componentSettings,
            componentVariablesOverwrites,
        });
    }
};
/**
 * Takes the initial set of properties, filters only design properties that will be mapped to CSS and
 * re-organizes them to be indexed by breakpoint ID ("breakpoint > variable > value"). It will
 * also resolve the design/ component values to plain values.
 *
 * **Example Input**
 * ```
 * variables = {
 *   cfMargin: { type: 'DesignValue', valuesByBreakpoint: { desktop: '1px', tablet: '2px' } },
 *   cfPadding: { type: 'DesignValue', valuesByBreakpoint: { desktop: '3px', mobile: '4px' } }
 * }
 * ```
 *
 * **Example Output**
 * ```
 * variableValuesByBreakpoints = {
 *   desktop: {
 *     cfMargin: '1px',
 *     cfPadding: '3px'
 *   },
 *   tablet: {
 *     cfMargin: '2px'
 *   },
 *   mobile: {
 *    cfPadding: '4px'
 *   }
 * }
 * ```
 *
 * **Note**
 * - The property cfBackgroundImageUrl is the only content property that gets mapped to CSS as well.
 *   It will be solely stored on the default breakpoint.
 * - For ComponentValues, it will either take the override from the pattern instance or fallback to
 *   the defaultValue defined in variableDefinitions.
 */
const indexByBreakpoint = ({ variables, breakpointIds, getBoundEntityById, unboundValues = {}, dataSource = {}, componentVariablesOverwrites, componentSettings = { variableDefinitions: {} }, }) => {
    const variableValuesByBreakpoints = breakpointIds.reduce((acc, breakpointId) => {
        return {
            ...acc,
            [breakpointId]: {},
        };
    }, {});
    const defaultBreakpoint = breakpointIds[0];
    for (const [variableName, variableData] of Object.entries(variables)) {
        // handling the special case - cfBackgroundImageUrl variable, which can be bound or unbound
        // so, we need to resolve it here and pass it down as a css property to be converted into the CSS
        // I used .startsWith() cause it can be part of a pattern node
        if (variableName === 'cfBackgroundImageUrl' ||
            // TODO: Test this for nested patterns as the name might be just a random hash without the actual name (needs to be validated).
            variableName.startsWith('cfBackgroundImageUrl_')) {
            const width = resolveVariable({
                variableData: variables['cfWidth'],
                defaultBreakpoint,
                componentSettings,
                componentVariablesOverwrites,
            });
            const options = resolveVariable({
                variableData: variables['cfBackgroundImageOptions'],
                defaultBreakpoint,
                componentSettings,
                componentVariablesOverwrites,
            });
            if (!options) {
                debug.error('[experiences-core::ssrStyles] Error transforming image asset: Required variable [cfBackgroundImageOptions] missing from component definition');
                continue;
            }
            const imageUrl = resolveBackgroundImageBinding({
                variableData,
                getBoundEntityById,
                unboundValues,
                dataSource,
                componentSettings,
                componentVariablesOverwrites,
                width,
                options,
            });
            if (imageUrl) {
                variableValuesByBreakpoints[defaultBreakpoint][variableName] = imageUrl;
            }
            continue;
        }
        let resolvedVariableData = variableData;
        if (variableData.type === 'ComponentValue') {
            const variableDefinition = componentSettings?.variableDefinitions[variableData.key];
            const defaultValue = variableDefinition.defaultValue;
            if (variableDefinition.group === 'style' && defaultValue !== undefined) {
                const overwriteVariableData = componentVariablesOverwrites?.[variableData.key];
                resolvedVariableData = mergeDefaultAndOverwriteValues(defaultValue, overwriteVariableData);
            }
        }
        if (resolvedVariableData.type !== 'DesignValue') {
            continue;
        }
        for (const [breakpointId, variableValue] of Object.entries(resolvedVariableData.valuesByBreakpoint)) {
            if (!isValidBreakpointValue(variableValue)) {
                continue;
            }
            variableValuesByBreakpoints[breakpointId] = {
                ...variableValuesByBreakpoints[breakpointId],
                [variableName]: variableValue,
            };
        }
    }
    return variableValuesByBreakpoints;
};
/**
 * Flattens the object from
 * `{ color: { [key]: [value] } }`
 * to
 * `{ 'color.key': [value] }`
 */
const flattenDesignTokenRegistry = (designTokenRegistry) => {
    return Object.entries(designTokenRegistry).reduce((acc, [categoryName, tokenCategory]) => {
        const tokensWithCategory = Object.entries(tokenCategory).reduce((acc, [tokenName, tokenValue]) => {
            return {
                ...acc,
                [`${categoryName}.${tokenName}`]: tokenValue,
            };
        }, {});
        return {
            ...acc,
            ...tokensWithCategory,
        };
    }, {});
};
function mergeDefaultAndOverwriteValues(defaultValue, overwriteValue) {
    if (defaultValue?.type === 'DesignValue' && overwriteValue?.type === 'DesignValue') {
        return mergeDesignValuesByBreakpoint(defaultValue, overwriteValue);
    }
    return overwriteValue ?? defaultValue;
}

/**
 * Turns a condition like `<768px` or `>1024px` into a media query rule.
 * For example, `<768px` becomes `max-width:768px` and `>1024px` becomes `min-width:1024px`.
 */
const toMediaQueryRule = (condition) => {
    const [evaluation, pixelValue] = [condition[0], condition.substring(1)];
    const mediaQueryRule = evaluation === '<' ? 'max-width' : 'min-width';
    return `(${mediaQueryRule}:${pixelValue})`;
};
/**
 * Converts a map of class names to CSS strings into a single CSS string.
 *
 * @param cssByClassName map of class names to CSS strings containing all rules for each class
 * @returns joined string of all CSS class definitions
 */
const toCompoundCss = (cssByClassName) => {
    return Object.entries(cssByClassName).reduce((acc, [className, css]) => {
        if (css === '')
            return acc;
        return `${acc}.${className}{${css}}`;
    }, ``);
};
/**
 * Create a single CSS string containing all class definitions for a given media query.
 *
 * @param cssByClassName map of class names to CSS strings containing all rules for each class
 * @param condition e.g. "*", "<520px", ">520px"
 * @param nextCondition optional next condition to create a disjunct media query that doesn't affect the next breakpoint
 * @returns joined string of all CSS class definitions wrapped into media queries
 */
const toMediaQuery = ({ cssByClassName, condition, nextCondition, }) => {
    const compoundCss = toCompoundCss(cssByClassName);
    if (compoundCss === '') {
        return '';
    }
    const queryRule = toMediaQueryRule(condition);
    if (!nextCondition) {
        if (condition === '*') {
            return compoundCss;
        }
        return `@media${queryRule}{${compoundCss}}`;
    }
    const nextRule = toMediaQueryRule(nextCondition);
    if (condition === '*') {
        return `@media not ${nextRule}{${compoundCss}}`;
    }
    return `@media${queryRule} and (not ${nextRule}){${compoundCss}}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function get(obj, path) {
    if (!path.length) {
        return obj;
    }
    try {
        const [currentPath, ...nextPath] = path;
        return get(obj[currentPath], nextPath);
    }
    catch (err) {
        return undefined;
    }
}

const getBoundValue = (entryOrAsset, path) => {
    const value = get(entryOrAsset, path.split('/').slice(2, -1));
    return value && typeof value == 'object' && value.url
        ? value.url
        : value;
};

const transformRichText = (entryOrAsset, entityStore, path) => {
    const value = getBoundValue(entryOrAsset, path);
    if (typeof value === 'string') {
        return {
            data: {},
            content: [
                {
                    nodeType: BLOCKS.PARAGRAPH,
                    data: {},
                    content: [
                        {
                            data: {},
                            nodeType: 'text',
                            value: value,
                            marks: [],
                        },
                    ],
                },
            ],
            nodeType: BLOCKS.DOCUMENT,
        };
    }
    if (typeof value === 'object' && value.nodeType === BLOCKS.DOCUMENT) {
        // resolve any links to assets/entries/hyperlinks
        // we need to clone, as we want to keep the original Entity in the EntityStore intact,
        // and resolveLinks() is mutating the node object.
        const richTextDocument = cloneDeep(value);
        resolveLinks(richTextDocument, entityStore);
        return richTextDocument;
    }
    return undefined;
};
const isLinkTarget = (node) => {
    return node?.data?.target?.sys?.type === 'Link';
};
const resolveLinks = (node, entityStore) => {
    if (!node)
        return;
    // Resolve link if current node has one
    if (isLinkTarget(node)) {
        const entity = entityStore.getEntityFromLink(node.data.target);
        if (entity) {
            node.data.target = entity;
        }
    }
    // Process content array if it exists
    if ('content' in node && Array.isArray(node.content)) {
        node.content.forEach((childNode) => resolveLinks(childNode, entityStore));
    }
};

const MAX_WIDTH_ALLOWED = 4000;
const getOptimizedImageAsset = ({ file, sizes, loading, quality = '100%', format, }) => {
    const qualityNumber = Number(quality.replace('%', ''));
    if (!validateParams(file, qualityNumber, format)) ;
    const url = file.url;
    const maxWidth = Math.min(file.details.image.width, MAX_WIDTH_ALLOWED);
    const numOfParts = Math.max(2, Math.ceil(maxWidth / 500));
    const widthParts = Array.from({ length: numOfParts }, (_, index) => Math.ceil((index + 1) * (maxWidth / numOfParts)));
    const srcSet = sizes
        ? widthParts.map((width) => `${getOptimizedImageUrl(url, width, qualityNumber, format)} ${width}w`)
        : [];
    const intrinsicImageWidth = file.details.image.width;
    if (intrinsicImageWidth > MAX_WIDTH_ALLOWED) {
        srcSet.push(`${getOptimizedImageUrl(url, undefined, qualityNumber, format)} ${intrinsicImageWidth}w`);
    }
    const returnedUrl = getOptimizedImageUrl(url, file.details.image.width > 2000 ? 2000 : undefined, qualityNumber, format);
    const optimizedImageAsset = {
        url: returnedUrl,
        srcSet,
        sizes,
        file,
        loading,
    };
    return optimizedImageAsset;
};

const transformMedia = (asset, variables, resolveDesignValue, variableName, path) => {
    let value;
    // If it is not a deep path and not pointing to the file of the asset,
    // it is just pointing to a normal field and therefore we just resolve the value as normal field
    if (!isDeepPath(path) && !lastPathNamedSegmentEq(path, 'file')) {
        return getBoundValue(asset, path);
    }
    //TODO: this will be better served by injectable type transformers instead of if statement
    if (variableName === 'cfImageAsset') {
        const optionsVariableName = 'cfImageOptions';
        const options = resolveDesignValue(variables[optionsVariableName]?.type === 'DesignValue'
            ? variables[optionsVariableName].valuesByBreakpoint
            : {});
        if (!options) {
            debug.error(`[experiences-core::transformMedia] Error transforming image asset: Required variable [${optionsVariableName}] missing from component definition`);
            return;
        }
        try {
            value = getOptimizedImageAsset({
                file: asset.fields.file,
                loading: options.loading,
                sizes: options.targetSize,
                quality: options.quality,
                format: options.format,
            });
            return value;
        }
        catch (error) {
            debug.error('[experiences-core::transformMedia] Error transforming image asset', error);
        }
        return;
    }
    if (variableName === 'cfBackgroundImageUrl') {
        let width = resolveDesignValue(variables['cfWidth']?.type === 'DesignValue' ? variables['cfWidth'].valuesByBreakpoint : {}, 'cfWidth') || '100%';
        const optionsVariableName = 'cfBackgroundImageOptions';
        const options = resolveDesignValue(variables[optionsVariableName]?.type === 'DesignValue'
            ? variables[optionsVariableName].valuesByBreakpoint
            : {});
        if (!options) {
            debug.error(`[experiences-core::transformMedia] Error transforming image asset: Required variable [${optionsVariableName}] missing from component definition`);
            return;
        }
        try {
            // Target width (px/rem/em) will be applied to the css url if it's lower than the original image width (in px)
            const assetDetails = asset.fields.file?.details;
            const assetWidth = assetDetails?.image?.width || 0; // This is always in px
            const targetWidthObject = parseCSSValue(options.targetSize); // Contains value and unit (px/rem/em) so convert and then compare to assetWidth
            const targetValue = targetWidthObject
                ? getTargetValueInPixels(targetWidthObject)
                : assetWidth;
            if (targetValue < assetWidth)
                width = `${targetValue}px`;
            value = getOptimizedBackgroundImageAsset(asset.fields.file, width, options.quality, options.format);
            return value;
        }
        catch (error) {
            debug.error('[experiences-core::transformMedia] Error transforming image asset', error);
        }
        return;
    }
    return asset.fields.file?.url;
};

const isExperienceEntry = (entry) => {
    return (entry?.sys?.type === 'Entry' &&
        !!entry.fields?.title &&
        !!entry.fields?.slug &&
        !!entry.fields?.componentTree &&
        Array.isArray(entry.fields.componentTree.breakpoints) &&
        Array.isArray(entry.fields.componentTree.children) &&
        typeof entry.fields.componentTree.schemaVersion === 'string');
};
const isPatternEntry = (entry) => {
    return isExperienceEntry(entry) && !!entry.fields?.componentSettings; // signals that this is pattern (not experience) entry
};
const isEntry = (value) => {
    return (null !== value &&
        typeof value === 'object' &&
        'sys' in value &&
        value.sys?.type === 'Entry');
};
const isAsset = (value) => {
    return (null !== value &&
        typeof value === 'object' &&
        'sys' in value &&
        value.sys?.type === 'Asset');
};
/**
 * Checks if the values is an array of links.
 * Note: we use convention where empty arrays are considered valid "arrays of links"
 * as they don't contradict the type definition.
 */
const isArrayOfLinks = (value) => {
    return Array.isArray(value) && value.every((item) => isLink(item));
};

function getResolvedEntryFromLink(entryOrAsset, path, entityStore) {
    if (isAsset(entryOrAsset)) {
        return entryOrAsset;
    }
    else if (!isEntry(entryOrAsset)) {
        throw new Error(`Expected an Entry or Asset, but got: ${JSON.stringify(entryOrAsset)}`);
    }
    const fieldName = path.split('/').slice(2, -1);
    const value = get(entryOrAsset, fieldName);
    let resolvedEntity;
    if (isAsset(value) || isEntry(value)) {
        // In some cases, reference fields are already resolved
        resolvedEntity = value;
    }
    else if (value?.sys.type === 'Link') {
        // Look up the reference in the entity store
        resolvedEntity = entityStore.getEntityFromLink(value);
    }
    else {
        debug.warn(`[experiences-core::getResolvedEntryFromLink] When attempting to follow link in field '${fieldName}' of entity, the value is expected to be a link, but got: ${JSON.stringify(value)}`, { entity: entryOrAsset });
        return;
    }
    // no need to make structuredClone(entityStore.getEntityFromLink(value)) because
    // we provide component with the original Object.frozen object of the entity.
    // As we don't resolve L3 and don't mutate the entity before returning anymore,
    // we don't need to make a copy of the entity. And even provide better referential integrity
    // for the component for the same entity.
    if (!resolvedEntity) {
        return;
    }
    return resolvedEntity;
}

const excludeUndefined = (value) => {
    return value !== undefined;
};
function getArrayValue(entryOrAsset, path, entityStore) {
    // NOTE: Not sure if we need this if-statement,
    // as it is NOT possible to bind to Array variable an Asset
    // (as Assets don't have multi-reference fields) unless it's a degenerate case.
    if (entryOrAsset.sys.type === 'Asset') {
        return entryOrAsset;
    }
    const fieldName = path.split('/').slice(2, -1);
    const arrayValue = get(entryOrAsset, fieldName);
    if (!isArray(arrayValue)) {
        debug.warn(`[experiences-core::getArrayValue] A field '${fieldName}' of an entity was bound to an Array variable. Expected value of that field to be an array, but got: ${JSON.stringify(arrayValue)}`, { entity: entryOrAsset });
        return;
    }
    const result = arrayValue
        .map((value) => {
        if (typeof value === 'string') {
            return value; // handles case where Text array is bound (in [Content Model] tab of the platform, select Text and make it a list)
        }
        else if (value?.sys?.type === 'Link') {
            const resolvedEntity = entityStore.getEntityFromLink(value);
            if (!resolvedEntity) {
                // We return undefined, which means that entity wasn't availble in the Entity Store due to:
                //  - because it's archived entity (and they normally wouldn't be sent to the Entity Store)
                //  - bug where some entity wasn't added to the Entity Store
                //  BTW, deleted entities shouldn't even be possible here as they require CT deletion first and that shouldn't allow us to load them at all)
                return;
            }
            return resolvedEntity;
        }
        else {
            debug.warn(`[experiences-core::getArrayValue] Expected value to be a string or Link, but got: ${JSON.stringify(value)}`);
            return undefined;
        }
    })
        .filter(excludeUndefined);
    // eg. imagine you have multi-referene field with 3 links to archived entries,
    //     all of them will be undefined on previous step and will be filtered out
    //     of resultWithoutUndefined. Instead of passing to component an empty array,
    //     we pass undefined. This means that develloper making custom component
    //     does not have to handle empty array case. But only undefiened, which signals:
    //     user didn't bind anything; user bound to reference field which is unset; all references are archived
    return result.length > 0 ? result : undefined;
}

const transformBoundContentValue = (variables, entityStore, binding, resolveDesignValue, variableName, variableType, path) => {
    const entityOrAsset = entityStore.getEntryOrAsset(binding, path);
    if (!entityOrAsset)
        return;
    switch (variableType) {
        case 'Media':
            // If we bound a normal entry field to the media variable we just return the bound value
            if (entityOrAsset.sys.type === 'Entry') {
                return getBoundValue(entityOrAsset, path);
            }
            return transformMedia(entityOrAsset, variables, resolveDesignValue, variableName, path);
        case 'RichText':
            return transformRichText(entityOrAsset, entityStore, path);
        case 'Array':
            return getArrayValue(entityOrAsset, path, entityStore);
        case 'Link':
            return getResolvedEntryFromLink(entityOrAsset, path, entityStore);
        default:
            return getBoundValue(entityOrAsset, path);
    }
};

function treeVisit(initialNode, onNode) {
    // returns last used index
    const _treeVisit = (currentNode, currentIndex, currentDepth) => {
        // Copy children in case of onNode removing it as we pass the node by reference
        const children = [...currentNode.children];
        onNode(currentNode, currentIndex, currentDepth);
        let nextAvailableIndex = currentIndex + 1;
        const lastUsedIndex = currentIndex;
        for (const child of children) {
            const lastUsedIndex = _treeVisit(child, nextAvailableIndex, currentDepth + 1);
            nextAvailableIndex = lastUsedIndex + 1;
        }
        return lastUsedIndex;
    };
    _treeVisit(initialNode, 0, 0);
}
/**
 * Traverses all nodes of a tree and maps all of them.
 * Intuitively works similarly to Array.map().
 * When returning mapped object from the mapper function
 * you _do not_ need to return about `.children[]` field.
 * This field is automatically managed by the treeMap() function.
 *
 * You can map each node to whatever you want, but shouldn't
 * modify the structure of the tree from the mapping function.
 */
function treeMap(node, onNode) {
    // Copy children in case of onNode removing it as we pass the node by reference
    const children = [...node.children];
    const newNode = {
        ...onNode(node),
        children: children.map((child) => treeMap(child, onNode)),
    };
    return newNode;
}

const getDataFromTree = (tree) => {
    let dataSource = {};
    let unboundValues = {};
    const queue = [...tree.root.children];
    while (queue.length) {
        const node = queue.shift();
        if (!node) {
            continue;
        }
        dataSource = { ...dataSource, ...node.data.dataSource };
        unboundValues = { ...unboundValues, ...node.data.unboundValues };
        if (node.children.length) {
            queue.push(...node.children);
        }
    }
    return {
        dataSource,
        unboundValues,
    };
};
const generateRandomId = (letterCount) => {
    const LETTERS = 'abcdefghijklmnopqvwxyzABCDEFGHIJKLMNOPQVWXYZ';
    const NUMS = '0123456789';
    const ALNUM = NUMS + LETTERS;
    const times = (n, callback) => Array.from({ length: n }, callback);
    const random = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    return times(letterCount, () => ALNUM[random(0, ALNUM.length - 1)]).join('');
};
const checkIsAssemblyNode = ({ componentId, usedComponents, }) => {
    if (!usedComponents?.length)
        return false;
    return usedComponents.some((usedComponent) => usedComponent.sys.id === componentId);
};
/**
 * This check assumes that the entry is already ensured to be an experience, i.e. the
 * content type of the entry is an experience type with the necessary annotations.
 **/
const checkIsAssemblyEntry = (entry) => {
    return Boolean(entry.fields?.componentSettings);
};
const checkIsAssemblyDefinition = (component) => component?.category === ASSEMBLY_DEFAULT_CATEGORY;
function parseCSSValue(input) {
    const regex = /^(\d+(\.\d+)?)(px|em|rem)$/;
    const match = input.match(regex);
    if (match) {
        return {
            value: parseFloat(match[1]),
            unit: match[3],
        };
    }
    return null;
}
function getTargetValueInPixels(targetWidthObject) {
    switch (targetWidthObject.unit) {
        case 'px':
            return targetWidthObject.value;
        case 'em':
            return targetWidthObject.value * 16;
        case 'rem':
            return targetWidthObject.value * 16;
        default:
            return targetWidthObject.value;
    }
}

class ParseError extends Error {
    constructor(message) {
        super(message);
    }
}
const isValidJsonObject = (s) => {
    try {
        const result = JSON.parse(s);
        if ('object' !== typeof result) {
            return false;
        }
        return true;
    }
    catch (e) {
        return false;
    }
};
const doesMismatchMessageSchema = (event) => {
    try {
        tryParseMessage(event);
        return false;
    }
    catch (e) {
        if (e instanceof ParseError) {
            return e.message;
        }
        throw e;
    }
};
const tryParseMessage = (event) => {
    if (!event.data) {
        throw new ParseError('Field event.data is missing');
    }
    if ('string' !== typeof event.data) {
        throw new ParseError(`Field event.data must be a string, instead of '${typeof event.data}'`);
    }
    if (!isValidJsonObject(event.data)) {
        throw new ParseError('Field event.data must be a valid JSON object serialized as string');
    }
    const eventData = JSON.parse(event.data);
    if (!eventData.source) {
        throw new ParseError(`Field eventData.source must be equal to 'composability-app'`);
    }
    if ('composability-app' !== eventData.source) {
        throw new ParseError(`Field eventData.source must be equal to 'composability-app', instead of '${eventData.source}'`);
    }
    // check eventData.eventType
    const supportedEventTypes = Object.values(INCOMING_EVENTS);
    if (!supportedEventTypes.includes(eventData.eventType)) {
        // Expected message: This message is handled in the EntityStore to store fetched entities
        if (eventData.eventType !== PostMessageMethods.REQUESTED_ENTITIES) {
            throw new ParseError(`Field eventData.eventType must be one of the supported values: [${supportedEventTypes.join(', ')}]`);
        }
    }
    return eventData;
};
const validateExperienceBuilderConfig = ({ locale, mode, }) => {
    if (mode === StudioCanvasMode.EDITOR || mode === StudioCanvasMode.READ_ONLY) {
        return;
    }
    if (!locale) {
        throw new Error('Parameter "locale" is required for experience builder initialization outside of editor mode');
    }
};

const uniqueById = (arr) => {
    const map = new Map();
    arr.forEach((item) => map.set(item.sys.id, item));
    return [...map.values()];
};
const isObject = (value) => {
    return typeof value === 'object' && value !== null;
};
/**
 * Extracts all references from an entry.
 * Handles both: reference and multi-reference fields.
 * Returns unique array of references (even if they repeat within the entry).
 */
const referencesOf = (entry, fnShouldFollowReferencesOfEntryField) => {
    const references = [];
    const handleArray = (fieldValue, _fieldName) => {
        for (const item of fieldValue) {
            if (isObject(item) && item.sys?.type === 'Link') {
                references.push(item);
            }
        }
    };
    const handleLink = (fieldValue, _fieldName) => {
        references.push(fieldValue);
    };
    for (const [fieldName, fieldValue] of Object.entries(entry.fields)) {
        if (fnShouldFollowReferencesOfEntryField &&
            !fnShouldFollowReferencesOfEntryField(fieldName, entry)) {
            continue;
        }
        if (fieldValue === undefined) {
            continue; // edge case when field is present on object, but is set to undefined explicitly e.g. during test mocking { myField: undefined }
        }
        if (Array.isArray(fieldValue)) {
            handleArray(fieldValue);
        }
        else if (fieldValue !== null &&
            isObject(fieldValue) &&
            fieldValue.sys?.type === 'Link') {
            handleLink(fieldValue);
        }
    }
    return uniqueById(references);
};
// -- REFERENCE EXTRACTION UTILITIES --
function extractReferencesFromEntriesAsIds(entries) {
    const [uniqueEntries, uniqueAssets, uniqueReferences] = extractReferencesFromEntries(entries);
    const entryIds = uniqueEntries.map((link) => link.sys.id);
    const assetIds = uniqueAssets.map((link) => link.sys.id);
    const referenceIds = uniqueReferences.map((link) => link.sys.id);
    return [entryIds, assetIds, referenceIds];
}
function extractReferencesFromEntries(entries) {
    const allReferences = entries.flatMap((entry) => referencesOf(entry));
    const uniqueReferences = uniqueById(allReferences);
    const uniqueAssets = uniqueReferences.filter((link) => link.sys.linkType === 'Asset');
    const uniqueEntries = uniqueReferences.filter((link) => link.sys.linkType === 'Entry');
    return [uniqueEntries, uniqueAssets, uniqueReferences];
}

const excludeAssets = (entity) => !isAsset(entity);
const excludePatternEntries = (entry) => !isPatternEntry(entry);
/**
 * Parses experience and extracts all leaf links that are referenced from the experience.
 * PRECONDITION: Relies on the fact that entityStore is preloaded with all dataSource
 * entries using include=2 (meaning that up to L3 entries are already preloaded into EntitStore).
 *
 * The function iterates over all entries in the entityStore (assuming they can be L1, L2, L3) and
 * over all of their references. Any references that are NOT available in the entityStore are considered
 * "leaf references" aka "leaf links" and are returned.
 *
 * The EntityStore happens to contain also entities representing patterns, which we do NOT consider
 * as entries that point to leaf links. So we don't iterate over patterns only over entries which
 * can be used for binding.
 */
const extractLeafLinksReferencedFromExperience = (experience) => {
    const assetLinks = [];
    const entryLinks = [];
    if (!experience.entityStore) {
        throw new Error('Parameter `experience` should have valid `experience.entityStore` object. Without it, we cannot extract leaf links. Most likely you passed `experience` instance that was not fully fetched. Check your experience fetching logic.');
    }
    // We want only leaf links which can be used for binding. We use two filters:
    // excludeAssets:         because assets do not have references, so we don't need to traverse them
    // excludePatternEntries: because EntityStore happens to also store pattern-entries.
    //                        Those point to other patterns, and we don't want to consider them as
    //                        parents of leaf links pointing to actual data carrying entries used for binding.
    const entries = experience.entityStore.entities
        .filter(excludeAssets)
        .filter(excludePatternEntries);
    // We assume that ALL of the entries in the experience
    for (const entry of entries) {
        const references = referencesOf(entry);
        for (const ref of references) {
            if (isLinkToAsset(ref)) {
                if (!experience.entityStore.getEntityFromLink(ref)) {
                    assetLinks.push(ref);
                }
            }
            else if (isLinkToEntry(ref)) {
                if (!experience.entityStore.getEntityFromLink(ref)) {
                    entryLinks.push(ref);
                }
            }
            else {
                debug.warn(`[experiences-core::experienceSchema] Unexpected reference type found in entry "${entry.sys.id}": ${JSON.stringify(ref)}`);
            }
        }
    }
    const dedupedAssetLinks = uniqueById(assetLinks);
    const dedupedEntryLinks = uniqueById(entryLinks);
    return {
        assetLinks: dedupedAssetLinks,
        entryLinks: dedupedEntryLinks,
        assetIds: dedupedAssetLinks.map((link) => link.sys.id),
        entryIds: dedupedEntryLinks.map((link) => link.sys.id),
    };
};

const splitDirectAndSlotChildren = (allChildNodes, componentDefinition) => {
    // Bridge difference between editor and delivery mode
    const getSlotId = (child) => {
        if ('data' in child)
            return child.data.slotId;
        return child.slotId;
    };
    const slotNodesMap = {};
    for (const slotId in componentDefinition.slots) {
        // We only allow one component per slot (container). This is just safer to not render components twice or not at all
        const nodes = allChildNodes.filter((child) => getSlotId(child) === slotId);
        slotNodesMap[slotId] = nodes.length ? nodes : null;
    }
    const directChildNodes = allChildNodes.filter((child) => getSlotId(child) === undefined);
    if (!componentDefinition.children || !directChildNodes.length) {
        // If there are no children allowed in the component or no children added, render as undefined
        return { slotNodesMap, directChildNodes: undefined };
    }
    return { slotNodesMap, directChildNodes };
};

const sendMessage = (eventType, data) => {
    if (typeof window === 'undefined') {
        return;
    }
    debug.debug(`[experiences-sdk-react::sendMessage] Sending message [${eventType}]`, {
        source: 'customer-app',
        eventType,
        payload: data,
    });
    window.parent?.postMessage({
        source: 'customer-app',
        eventType,
        payload: data,
    }, '*');
};

function deepFreeze(obj) {
    const propNames = Object.getOwnPropertyNames(obj);
    for (const name of propNames) {
        const value = obj[name];
        if (value && typeof value === 'object') {
            deepFreeze(value);
        }
    }
    return Object.freeze(obj);
}

/**
 * Base Store for entities
 * Can be extended for the different loading behaviours (editor, production, ..)
 */
class EntityStoreBase {
    constructor({ entities, locale }) {
        /* serialized */ this.entryMap = new Map();
        /* serialized */ this.assetMap = new Map();
        this.locale = locale;
        for (const entity of entities) {
            this.addEntity(entity);
        }
    }
    get entities() {
        return [...this.entryMap.values(), ...this.assetMap.values()];
    }
    updateEntity(entity) {
        this.addEntity(entity);
    }
    getEntryOrAsset(linkOrEntryOrAsset, path) {
        if (isDeepPath(path)) {
            return this.getDeepEntry(linkOrEntryOrAsset, path);
        }
        let entity;
        if (isLink(linkOrEntryOrAsset)) {
            const resolvedEntity = linkOrEntryOrAsset.sys.linkType === 'Entry'
                ? this.entryMap.get(linkOrEntryOrAsset.sys.id)
                : this.assetMap.get(linkOrEntryOrAsset.sys.id);
            if (!resolvedEntity || resolvedEntity.sys.type !== linkOrEntryOrAsset.sys.linkType) {
                debug.warn(`[experiences-core::EntityStoreBase] Experience references unresolved entity: ${JSON.stringify(linkOrEntryOrAsset)}`);
                return;
            }
            entity = resolvedEntity;
        }
        else if (isAsset(linkOrEntryOrAsset) || isEntry(linkOrEntryOrAsset)) {
            // We already have the complete entity in preview & delivery (resolved by the CMA client)
            entity = linkOrEntryOrAsset;
        }
        else {
            throw new Error(`[experiences-core::EntityStoreBase] Unexpected object when resolving entity: ${JSON.stringify(linkOrEntryOrAsset)}`);
        }
        return entity;
    }
    /**
     * @deprecated in the base class this should be simply an abstract method
     * @param entityLink
     * @param path
     * @returns
     */
    getValue(entityLink, path) {
        const entity = this.getEntity(entityLink.sys.linkType, entityLink.sys.id);
        if (!entity) {
            // TODO: move to `debug` utils once it is extracted
            debug.warn(`Unresolved entity reference: ${entityLink.sys.linkType} with ID ${entityLink.sys.id}`);
            return;
        }
        return get(entity, path);
    }
    getEntityFromLink(link) {
        const resolvedEntity = link.sys.linkType === 'Entry'
            ? this.entryMap.get(link.sys.id)
            : this.assetMap.get(link.sys.id);
        if (!resolvedEntity || resolvedEntity.sys.type !== link.sys.linkType) {
            debug.warn(`[experiences-core::EntityStoreBase] Experience references unresolved entity: ${JSON.stringify(link)}`);
            return;
        }
        return resolvedEntity;
    }
    getAssetById(assetId) {
        const asset = this.assetMap.get(assetId);
        if (!asset) {
            debug.warn(`[experiences-core::EntityStoreBase] Asset with ID "${assetId}" is not found in the store`);
            return;
        }
        return asset;
    }
    getEntryById(entryId) {
        const entry = this.entryMap.get(entryId);
        if (!entry) {
            debug.warn(`[experiences-core::EntityStoreBase] Entry with ID "${entryId}" is not found in the store`);
            return;
        }
        return entry;
    }
    getEntitiesFromMap(type, ids) {
        const resolved = [];
        const missing = [];
        for (const id of ids) {
            const entity = this.getEntity(type, id);
            if (entity) {
                resolved.push(entity);
            }
            else {
                missing.push(id);
            }
        }
        return {
            resolved,
            missing,
        };
    }
    addEntity(entity) {
        if (isAsset(entity)) {
            // cloned and frozen
            this.assetMap.set(entity.sys.id, deepFreeze(cloneDeep(entity)));
        }
        else if (isEntry(entity)) {
            // cloned and frozen
            this.entryMap.set(entity.sys.id, deepFreeze(cloneDeep(entity)));
        }
        else {
            throw new Error(`Attempted to add an entity to the store that is neither Asset nor Entry: '${JSON.stringify(entity)}'`);
        }
    }
    async fetchAsset(id) {
        const { resolved, missing } = this.getEntitiesFromMap('Asset', [id]);
        if (missing.length) {
            // TODO: move to `debug` utils once it is extracted
            debug.warn(`[experiences-core::EntityStoreBase] Asset "${id}" is not in the store`);
            return;
        }
        return resolved[0];
    }
    async fetchAssets(ids) {
        const { resolved, missing } = this.getEntitiesFromMap('Asset', ids);
        if (missing.length) {
            throw new Error(`Missing assets in the store (${missing.join(',')})`);
        }
        return resolved;
    }
    async fetchEntry(id) {
        const { resolved, missing } = this.getEntitiesFromMap('Entry', [id]);
        if (missing.length) {
            // TODO: move to `debug` utils once it is extracted
            debug.warn(`[experiences-core::EntityStoreBase] Entry "${id}" is not in the store`);
            return;
        }
        return resolved[0];
    }
    async fetchEntries(ids) {
        const { resolved, missing } = this.getEntitiesFromMap('Entry', ids);
        if (missing.length) {
            throw new Error(`Missing assets in the store (${missing.join(',')})`);
        }
        return resolved;
    }
    getDeepEntry(linkOrEntryOrAsset, path) {
        const resolveFieldset = (unresolvedFieldset, headEntry) => {
            const resolvedFieldset = [];
            let entityToResolveFieldsFrom = headEntry;
            for (let i = 0; i < unresolvedFieldset.length; i++) {
                const isLeaf = i === unresolvedFieldset.length - 1; // with last row, we are not expecting a link, but a value
                const row = unresolvedFieldset[i];
                const [, field, _localeQualifier] = row;
                if (!entityToResolveFieldsFrom) {
                    throw new Error(`Logic Error: Cannot resolve field ${field} of a fieldset as there is no entity to resolve it from.`);
                }
                if (isLeaf) {
                    resolvedFieldset.push([entityToResolveFieldsFrom, field, _localeQualifier]);
                    break;
                }
                const fieldValue = get(entityToResolveFieldsFrom, ['fields', field]);
                if (undefined === fieldValue) {
                    return {
                        resolvedFieldset,
                        isFullyResolved: false,
                        reason: `Cannot resolve field Link<${entityToResolveFieldsFrom.sys.type}>(sys.id=${entityToResolveFieldsFrom.sys.id}).fields[${field}] as field value is not defined`,
                    };
                }
                else if (isLink(fieldValue)) {
                    const entity = this.getEntityFromLink(fieldValue);
                    if (entity === undefined) {
                        return {
                            resolvedFieldset,
                            isFullyResolved: false,
                            reason: `Field reference Link (sys.id=${fieldValue.sys.id}) not found in the EntityStore, waiting...`,
                        };
                    }
                    resolvedFieldset.push([entityToResolveFieldsFrom, field, _localeQualifier]);
                    entityToResolveFieldsFrom = entity; // we move up
                }
                else if (isAsset(fieldValue) || isEntry(fieldValue)) {
                    resolvedFieldset.push([entityToResolveFieldsFrom, field, _localeQualifier]);
                    entityToResolveFieldsFrom = fieldValue; // we move up
                }
                else {
                    return {
                        resolvedFieldset,
                        isFullyResolved: false,
                        reason: `Deep path points to an invalid field value of type '${typeof fieldValue}' (value=${fieldValue})`,
                    };
                }
            }
            return {
                resolvedFieldset,
                isFullyResolved: true,
            };
        };
        const headEntity = isLink(linkOrEntryOrAsset)
            ? this.getEntityFromLink(linkOrEntryOrAsset)
            : linkOrEntryOrAsset;
        if (undefined === headEntity) {
            return;
        }
        const unresolvedFieldset = parseDataSourcePathIntoFieldset(path);
        // The purpose here is to take this intermediate representation of the deep-path
        // and to follow the links to the leaf-entity and field
        // in case we can't follow till the end, we should signal that there was null-reference in the path
        const { resolvedFieldset, isFullyResolved, reason } = resolveFieldset(unresolvedFieldset, headEntity);
        if (!isFullyResolved) {
            if (reason) {
                debug.log(`[experiences-core::EntityStoreBase] Deep path wasn't resolved till leaf node, falling back to undefined, because: ${reason}`);
            }
            return;
        }
        const [leafEntity] = resolvedFieldset[resolvedFieldset.length - 1];
        return leafEntity;
    }
    getEntity(type, id) {
        if (type === 'Asset') {
            return this.assetMap.get(id);
        }
        return this.entryMap.get(id);
    }
    toJSON() {
        return {
            entryMap: Object.fromEntries(this.entryMap),
            assetMap: Object.fromEntries(this.assetMap),
            locale: this.locale,
        };
    }
}

/**
 * EntityStore which resolves entries and assets from the editor
 * over the sendMessage and subscribe functions.
 */
class EditorEntityStore extends EntityStoreBase {
    constructor({ entities, locale, sendMessage, subscribe, timeoutDuration = 3000, }) {
        super({ entities, locale });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.requestCache = new Map();
        this.cacheIdSeperator = ',';
        this.sendMessage = sendMessage;
        this.subscribe = subscribe;
        this.timeoutDuration = timeoutDuration;
    }
    cleanupPromise(referenceId) {
        setTimeout(() => {
            this.requestCache.delete(referenceId);
        }, 300);
    }
    getCacheId(id) {
        return id.length === 1 ? id[0] : id.join(this.cacheIdSeperator);
    }
    async fetchEntity(type, ids, skipCache = false) {
        let missing;
        if (!skipCache) {
            const { missing: missingFromCache, resolved } = this.getEntitiesFromMap(type, ids);
            if (missingFromCache.length === 0) {
                // everything is already in cache
                return resolved;
            }
            missing = missingFromCache;
        }
        else {
            missing = [...ids];
        }
        const cacheId = this.getCacheId(missing);
        const openRequest = this.requestCache.get(cacheId);
        if (openRequest) {
            return openRequest;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const newPromise = new Promise((resolve, reject) => {
            const unsubscribe = this.subscribe(PostMessageMethods.REQUESTED_ENTITIES, (message) => {
                const messageIds = [
                    ...message.entities.map((entity) => entity.sys.id),
                    ...(message.missingEntityIds ?? []),
                ];
                if (missing.every((id) => messageIds.includes(id))) {
                    clearTimeout(timeout);
                    resolve(message.entities);
                    this.cleanupPromise(cacheId);
                    ids.forEach((id) => this.cleanupPromise(id));
                    unsubscribe();
                }
                else {
                    debug.warn('[experiences-core::EditorEntityStore] Unexpected entities received in REQUESTED_ENTITIES. Ignoring this response.');
                }
            });
            const timeout = setTimeout(() => {
                reject(new Error(`Request for entities timed out ${this.timeoutDuration}ms for ${cacheId}`));
                this.cleanupPromise(cacheId);
                ids.forEach((id) => this.cleanupPromise(id));
                unsubscribe();
            }, this.timeoutDuration);
            this.sendMessage(PostMessageMethods.REQUEST_ENTITIES, {
                entityIds: missing,
                entityType: type,
                locale: this.locale,
            });
        });
        this.requestCache.set(cacheId, newPromise);
        ids.forEach((cid) => {
            this.requestCache.set(cid, newPromise);
        });
        const result = (await newPromise);
        result.forEach((value) => {
            this.addEntity(value);
        });
        return this.getEntitiesFromMap(type, ids).resolved;
    }
    async fetchAsset(id, skipCache = false) {
        try {
            return (await this.fetchAssets([id], skipCache))[0];
        }
        catch (err) {
            // TODO: move to debug utils once it is extracted
            debug.warn(`[experiences-core::EditorEntityStore] Failed to request asset ${id}`);
            return undefined;
        }
    }
    fetchAssets(ids, skipCache = false) {
        return this.fetchEntity('Asset', ids, skipCache);
    }
    async fetchEntry(id, skipCache = false) {
        try {
            return (await this.fetchEntries([id], skipCache))[0];
        }
        catch (err) {
            // TODO: move to debug utils once it is extracted
            debug.warn(`[experiences-core::EditorEntityStore] Failed to request entry ${id}`, err);
            return undefined;
        }
    }
    fetchEntries(ids, skipCache = false) {
        return this.fetchEntity('Entry', ids, skipCache);
    }
}

function transformAssetFileToUrl(fieldValue) {
    return fieldValue && typeof fieldValue == 'object' && fieldValue.url
        ? fieldValue.url
        : fieldValue;
}

// The default of 3s in the EditorEntityStore is sometimes timing out and
// leads to not rendering bound content and assemblies.
const REQUEST_TIMEOUT = 10000;
class EditorModeEntityStore extends EditorEntityStore {
    constructor({ entities, locale }) {
        debug.debug(`[experiences-core::EditorModeEntityStore] Initializing editor entity store with ${entities.length} entities for locale ${locale}.`, { entities });
        const subscribe = (method, cb) => {
            const handleMessage = (event) => {
                const data = JSON.parse(event.data);
                if (typeof data !== 'object' || !data)
                    return;
                if (data.source !== 'composability-app')
                    return;
                if (data.eventType === method) {
                    cb(data.payload);
                }
            };
            if (typeof window !== 'undefined') {
                window.addEventListener('message', handleMessage);
            }
            return () => {
                if (typeof window !== 'undefined') {
                    window.removeEventListener('message', handleMessage);
                }
            };
        };
        super({ entities, sendMessage, subscribe, locale, timeoutDuration: REQUEST_TIMEOUT });
    }
    /**
     * This function collects and returns the list of requested entries and assets. Additionally, it checks
     * upfront whether any async fetching logic is actually happening. If not, it returns a plain `false` value, so we
     * can detect this early and avoid unnecessary re-renders.
     * @param entityLinks
     * @returns false if no async fetching is happening, otherwise a promise that resolves when all entities are fetched
     */
    async fetchEntities({ missingEntryIds, missingAssetIds, skipCache = false, }) {
        // Entries and assets will be stored in entryMap and assetMap
        await Promise.all([
            super.fetchEntries(missingEntryIds, skipCache),
            super.fetchAssets(missingAssetIds, skipCache),
        ]);
    }
    getMissingEntityIds(entityLinks) {
        const entryLinks = entityLinks.filter((link) => link.sys?.linkType === 'Entry');
        const assetLinks = entityLinks.filter((link) => link.sys?.linkType === 'Asset');
        const uniqueEntryIds = [...new Set(entryLinks.map((link) => link.sys.id))];
        const uniqueAssetIds = [...new Set(assetLinks.map((link) => link.sys.id))];
        const { missing: missingEntryIds } = this.getEntitiesFromMap('Entry', uniqueEntryIds);
        const { missing: missingAssetIds } = this.getEntitiesFromMap('Asset', uniqueAssetIds);
        return { missingEntryIds, missingAssetIds };
    }
    getValue(entityLinkOrEntity, path) {
        const entity = this.getEntryOrAsset(entityLinkOrEntity, path.join('/'));
        if (!entity) {
            return;
        }
        const fieldValue = get(entity, path);
        return transformAssetFileToUrl(fieldValue);
    }
}

const resolveDeepUsedComponents = ({ experienceEntryFields, parentComponents, }) => {
    const totalUsedComponents = [];
    const usedComponents = experienceEntryFields?.usedComponents;
    if (!usedComponents || usedComponents.length === 0) {
        return [];
    }
    for (const component of usedComponents) {
        if ('fields' in component) {
            totalUsedComponents.push(component);
            if (parentComponents.has(component.sys.id)) {
                continue;
            }
            totalUsedComponents.push(...resolveDeepUsedComponents({
                experienceEntryFields: component.fields,
                parentComponents: new Set([...parentComponents, component.sys.id]),
            }));
        }
    }
    return totalUsedComponents;
};

class EntityStore extends EntityStoreBase {
    constructor(options) {
        if (typeof options === 'string') {
            // For SSR/SSG, the entity store is created server-side and passed to the client as a serialised JSON.
            // So the properties in data.entityStore are equal to the attributes of this class (see `toJSON()`)
            const serializedAttributes = JSON.parse(options).entityStore;
            super({
                entities: [
                    ...Object.values(serializedAttributes.entryMap),
                    ...Object.values(serializedAttributes.assetMap),
                ],
                locale: serializedAttributes.locale,
            });
            this._hoistedParameterDefinitions = {};
            this._hoistedVariableMappings = {};
            this._experienceEntryFields = serializedAttributes._experienceEntryFields; // non-broken deserialization always should result in valid ExperienceFields
            this._experienceEntryId = serializedAttributes._experienceEntryId; // // non-broken deserialization always should result in valid experienceEntryId
            this._unboundValues = serializedAttributes._unboundValues; // non-broken deserialization always should result in valid unboundValues
        }
        else {
            const { experienceEntry, entities, locale } = options;
            if (!isExperienceEntry(experienceEntry)) {
                throw new Error('Provided entry is not an experience entry');
            }
            super({ entities, locale });
            this._hoistedParameterDefinitions = {};
            this._hoistedVariableMappings = {};
            this._experienceEntryFields = experienceEntry.fields;
            this._experienceEntryId = experienceEntry.sys.id;
            this._unboundValues = experienceEntry.fields.unboundValues;
        }
        // DERIVE ENTITY STORE INSTANCE VARIBLES
        // Register prebindings
        {
            const usedComponentLinks = this._experienceEntryFields.usedComponents ?? [];
            const usedComponents = usedComponentLinks
                .map((component) => (isLink(component) ? this.getEntityFromLink(component) : component))
                .filter((component) => component !== undefined);
            this._hoistedParameterDefinitions = EntityStore.calculateHoistedParameterDefinitions(this._experienceEntryFields, usedComponents);
            this._hoistedVariableMappings = EntityStore.calculateHoistedVariableMappings(this._experienceEntryFields, usedComponents);
        }
        // Register deep references
        this._usedComponentsWithDeepReferences = resolveDeepUsedComponents({
            experienceEntryFields: this._experienceEntryFields,
            parentComponents: new Set([this._experienceEntryId]),
        });
    }
    static calculateHoistedParameterDefinitions(parentEntryFields, usedComponents) {
        let hoistedDefinitions = {};
        // --------------------
        // Hoist prebinding for the L1 parent pattern aka `pA`
        // --------------------
        hoistedDefinitions = Object.assign(hoistedDefinitions, parentEntryFields.componentSettings?.prebindingDefinitions?.[0].parameterDefinitions || {});
        // --------------------
        // Hoist prebinding for the L2 nested patterns, patterns aka`pB`
        // --------------------
        usedComponents.forEach((patternEntryLevel2) => {
            hoistedDefinitions = {
                ...hoistedDefinitions,
                ...patternEntryLevel2.fields.componentSettings?.prebindingDefinitions?.[0]
                    .parameterDefinitions,
            };
        });
        // --------------------
        // Hoist prebinding for L3 nested patterns, patterns aka `pC`
        // --------------------
        const usedComponentsLevel3 = usedComponents.flatMap((patternEntryLevel2) => {
            const usedComponents = patternEntryLevel2.fields.usedComponents || [];
            const filteredUsedComponents = usedComponents.filter((component) => component !== undefined && isExperienceEntry(component));
            return filteredUsedComponents;
        });
        usedComponentsLevel3.forEach((patternEntryLevel3) => {
            hoistedDefinitions = {
                ...hoistedDefinitions,
                ...patternEntryLevel3.fields.componentSettings?.prebindingDefinitions?.[0]
                    .parameterDefinitions,
            };
        });
        return hoistedDefinitions;
    }
    static calculateHoistedVariableMappings(parentEntryFields, usedComponents) {
        let hoistedMappings = {};
        // --------------------
        // Hoist prebinding for the L1 parent pattern aka `pA`
        // --------------------
        hoistedMappings = Object.assign(hoistedMappings, parentEntryFields.componentSettings?.prebindingDefinitions?.[0].variableMappings || {});
        // --------------------
        // Hoist prebinding for the L2 nested patterns, patterns aka`pB`
        // --------------------
        usedComponents.forEach((patternEntryLevel2) => {
            hoistedMappings = {
                ...hoistedMappings,
                ...patternEntryLevel2.fields.componentSettings?.prebindingDefinitions?.[0].variableMappings,
            };
        });
        // --------------------
        // Hoist prebinding for L3 nested patterns, patterns aka `pC`
        // --------------------
        const usedComponentsLevel3 = usedComponents.flatMap((patternEntryLevel2) => {
            const usedComponents = patternEntryLevel2.fields.usedComponents || [];
            const filteredUsedComponents = usedComponents.filter((component) => component !== undefined && isExperienceEntry(component));
            return filteredUsedComponents;
        });
        usedComponentsLevel3.forEach((patternEntryLevel3) => {
            hoistedMappings = {
                ...hoistedMappings,
                ...patternEntryLevel3.fields.componentSettings?.prebindingDefinitions?.[0].variableMappings,
            };
        });
        return hoistedMappings;
    }
    getCurrentLocale() {
        return this.locale;
    }
    get hoistedVariableMappings() {
        return this._hoistedVariableMappings;
    }
    get hoistedParameterDefinitions() {
        return this._hoistedParameterDefinitions;
    }
    get experienceEntryFields() {
        return this._experienceEntryFields;
    }
    get experienceEntryId() {
        return this._experienceEntryId;
    }
    get schemaVersion() {
        return this._experienceEntryFields?.componentTree.schemaVersion;
    }
    get breakpoints() {
        return this._experienceEntryFields?.componentTree.breakpoints ?? [];
    }
    get dataSource() {
        return this._experienceEntryFields?.dataSource ?? {};
    }
    get unboundValues() {
        return this._unboundValues ?? {};
    }
    get usedComponents() {
        return this._usedComponentsWithDeepReferences ?? [];
    }
    /**
     * Extend the existing set of unbound values with the ones from the assembly definition.
     * When creating a new assembly out of a container, the unbound value keys are copied and
     * thus the existing and the added ones have colliding keys. In the case of overlapping value
     * keys, the ones from the experience overrule the ones from the assembly definition as
     * the latter one is certainly just a default value while the other one is from the actual instance.
     * @param unboundValues set of unbound values defined in the assembly definition
     */
    addAssemblyUnboundValues(unboundValues) {
        this._unboundValues = { ...unboundValues, ...(this._unboundValues ?? {}) };
    }
    getValue(entityLinkOrEntity, path) {
        const entity = isLink(entityLinkOrEntity)
            ? this.getEntityFromLink(entityLinkOrEntity)
            : entityLinkOrEntity;
        if (entity === undefined) {
            return;
        }
        const fieldValue = get(entity, path);
        return transformAssetFileToUrl(fieldValue);
    }
    toJSON() {
        return {
            _experienceEntryFields: this._experienceEntryFields,
            _experienceEntryId: this._experienceEntryId,
            _unboundValues: this._unboundValues,
            ...super.toJSON(),
        };
    }
}

class UninitializedEntityStore extends EntityStoreBase {
    constructor() {
        super({ entities: [], locale: 'uninitialized-locale-in-uninitialized-entity-store' });
    }
}

const inMemoryEntitiesStore = create((set, get) => ({
    // The UninitializedEntityStore is a placeholder instance and is here to highlight the
    // // fact that it's not used by anything until during loading lifecycle it'sreplaced by real entity store:
    //   - in Preview+Delivery mode: right after we fetch Expereince and it entities
    //   - in EDITOR (VisualEditor) mode: right after the VisualEditor is async imported and initialize event happens
    entityStore: new UninitializedEntityStore(),
    areEntitiesFetched: false,
    setEntitiesFetched(fetched) {
        set({ areEntitiesFetched: fetched });
    },
    resolveAssetById(assetId) {
        if (!assetId)
            return undefined;
        const { entityStore } = get();
        return entityStore.getAssetById(assetId);
    },
    resolveEntryById(entryId) {
        if (!entryId)
            return undefined;
        const { entityStore } = get();
        return entityStore.getEntryById(entryId);
    },
    resolveEntity(link) {
        if (!link)
            return undefined;
        const { entityStore } = get();
        return entityStore.getEntityFromLink(link);
    },
    resetEntityStore(entityStore) {
        set({
            entityStore,
            areEntitiesFetched: false,
        });
    },
}));

function maybeResolveLink(maybeLink) {
    if (!isLink(maybeLink)) {
        debug.warn('[experiences-core::InMemoryEntitiesStore] maybeResolveLink function must receive Link shape. Provided argument does not match the Link shape: ', maybeLink);
        return undefined;
    }
    return inMemoryEntitiesStore.getState().resolveEntity(maybeLink);
}
function maybeResolveByAssetId(assetId) {
    return inMemoryEntitiesStore.getState().resolveAssetById(assetId);
}
function maybeResolveByEntryId(entryId) {
    return inMemoryEntitiesStore.getState().resolveEntryById(entryId);
}
function hasEntry(entryId) {
    return Boolean(maybeResolveByEntryId(entryId));
}
function hasAsset(assetId) {
    return Boolean(maybeResolveByAssetId(assetId));
}
function addEntities(entities) {
    if (!Array.isArray(entities) || entities.length === 0) {
        return;
    }
    const { entityStore } = inMemoryEntitiesStore.getState();
    const definedEntities = entities.filter(Boolean);
    for (const entity of definedEntities) {
        entityStore.updateEntity(entity);
    }
}
const inMemoryEntities = {
    maybeResolveLink,
    maybeResolveByAssetId,
    maybeResolveByEntryId,
    hasEntry,
    hasAsset,
    addEntities,
};
const useInMemoryEntities = () => {
    return inMemoryEntities;
};

var VisualEditorMode;
(function (VisualEditorMode) {
    VisualEditorMode["LazyLoad"] = "lazyLoad";
    VisualEditorMode["InjectScript"] = "injectScript";
})(VisualEditorMode || (VisualEditorMode = {}));

function createExperience(options) {
    let entityStore;
    if (typeof options === 'string') {
        entityStore = new EntityStore(options);
    }
    else {
        const { experienceEntry, referencedAssets, referencedEntries, locale } = options;
        if ([experienceEntry, ...referencedAssets, ...referencedEntries].some(isNotLocalized)) {
            throw new Error('Some of the provided content is not localized. Please localize every entity before passing it to this function. Note that this is solely determined by `sys.locale` being set respectively.');
        }
        if (!isExperienceEntry(experienceEntry)) {
            throw new Error('Provided entry is not an experience entry');
        }
        entityStore = new EntityStore({
            experienceEntry,
            entities: [...referencedEntries, ...referencedAssets],
            locale,
        });
    }
    inMemoryEntitiesStore.getState().resetEntityStore(entityStore);
    return {
        entityStore,
    };
}
// Following the API shape, we check the `sys.locale` property as we can't rely on the shape of
// fields to determine whether it's localized or not.
// See CDA documentation mentioning it here: https://www.contentful.com/developers/docs/references/content-delivery-api/#/introduction/common-resource-attributes
const isNotLocalized = (entity) => {
    return !entity.sys.locale;
};

/**
 * Fetches an experience entry by its slug or id. Throws an error if there are multiple
 * entries with the same slug. Additionally, it resolves all nested pattern entries inside `fields.usedComponents`.
 * @param options.client - Instantiated client from the Contentful SDK. If this is using the `withAllLocales` modifier, you may not provide a specific locale.
 * @param options.locale - Provide a locale if your experience contains custom localized fields. Otherwise, it will fallback to the default locale.
 * @param options.experienceTypeId - id of the content type associated with the experience
 * @param options.identifier - identifying condition to find the correct experience entry
 *
 */
const fetchExperienceEntry = async ({ client, experienceTypeId, locale, identifier, }) => {
    if (!client) {
        throw new Error('Failed to fetch experience entities. Required "client" parameter was not provided');
    }
    if (!experienceTypeId) {
        throw new Error('Failed to fetch experience entities. Required "experienceTypeId" parameter was not provided');
    }
    if (!('slug' in identifier) && !('id' in identifier)) {
        throw new Error(`Failed to fetch experience entities. At least one identifier must be provided. Received: ${JSON.stringify(identifier)}`);
    }
    const filter = 'slug' in identifier ? { 'fields.slug': identifier.slug } : { 'sys.id': identifier.id };
    const entries = await client.getEntries({
        content_type: experienceTypeId,
        locale,
        include: 3, // fetching max 3 level deep references due to nested patterns
        ...filter,
    });
    if (entries.items.length > 1) {
        throw new Error(`More than one experience with identifier: ${JSON.stringify(identifier)} was found`);
    }
    return entries.items[0];
};

class DeepReference {
    constructor({ path, dataSource }) {
        const { key, field, referentField } = parseDataSourcePathWithL1DeepBindings(path);
        this.originalPath = path;
        this.entityId = dataSource[key].sys.id;
        this.entityLink = dataSource[key];
        this.field = field;
        this.referentField = referentField;
    }
    get headEntityId() {
        return this.entityId;
    }
    /**
     * Extracts referent from the path, using EntityStore as source of
     * entities during the resolution path.
     */
    extractReferent(entityStore) {
        const headEntity = entityStore.getEntityFromLink(this.entityLink);
        const maybeReferentLink = headEntity?.fields[this.field];
        if (undefined === maybeReferentLink) {
            // field references nothing (or even field doesn't exist)
            return undefined;
        }
        if (!isLink(maybeReferentLink)) {
            // Scenario of "impostor referent", where one of the deepPath's segments is not a Link but some other type
            // Under normal circumstance we expect field to be a Link, but it could be an "impostor"
            // eg. `Text` or `Number` or anything like that; could be due to CT changes or manual path creation via CMA
            return undefined;
        }
        return maybeReferentLink;
    }
    static from(opt) {
        return new DeepReference(opt);
    }
}
function gatherDeepReferencesFromExperienceEntry(experienceEntry) {
    const deepReferences = [];
    const dataSource = experienceEntry.fields.dataSource;
    const { children } = experienceEntry.fields.componentTree;
    treeVisit({
        definitionId: 'root',
        variables: {},
        children,
    }, (node) => {
        if (!node.variables)
            return;
        for (const [, variableMapping] of Object.entries(node.variables)) {
            if (variableMapping.type !== 'BoundValue')
                continue;
            if (!isDeepPath(variableMapping.path))
                continue;
            deepReferences.push(DeepReference.from({
                path: variableMapping.path,
                dataSource,
            }));
        }
    });
    return deepReferences;
}
function gatherDeepReferencesFromTree(startingNode, dataSource, getEntityFromLink) {
    const deepReferences = [];
    treeVisit(startingNode, (node) => {
        if (!node.data.props)
            return;
        for (const [, variableMapping] of Object.entries(node.data.props)) {
            if (variableMapping.type !== 'BoundValue')
                continue;
            if (isDeepPath(variableMapping.path)) {
                deepReferences.push(DeepReference.from({
                    path: variableMapping.path,
                    dataSource,
                }));
            }
            else if (isPreboundProp(variableMapping) && isDeepPrebinding(variableMapping)) {
                const getEntityByDataSourceKey = (dataSourceKey) => {
                    const entityLink = dataSource[dataSourceKey];
                    if (!entityLink)
                        return undefined;
                    return getEntityFromLink(entityLink);
                };
                const deepPrebindingPath = getPrebindingPathBySourceEntry(variableMapping, getEntityByDataSourceKey);
                if (!deepPrebindingPath)
                    continue;
                deepReferences.push(DeepReference.from({
                    path: deepPrebindingPath,
                    dataSource,
                }));
            }
        }
    });
    return deepReferences;
}

/**
 * Traverses deep-references and extracts referents from valid deep-paths.
 * The referents are received from the CDA/CPA response `.includes` field.
 *
 * In case deep-paths not resolving till the end, eg.:
 *  - non-link referents: are ignored
 *  - unset references:   are ignored
 *
 * Errors are thrown in case of deep-paths being correct,
 * but referents not found. Because if we don't throw now, the EntityStore will
 * be missing entities and upon rendering will not be able to render bindings.
 */
function gatherAutoFetchedReferentsFromIncludes(deepReferences, entriesResponse) {
    const autoFetchedReferentEntries = [];
    const autoFetchedReferentAssets = [];
    for (const reference of deepReferences) {
        const headEntry = entriesResponse.items.find((entry) => entry.sys.id === reference.headEntityId);
        if (!headEntry) {
            debug.debug(`[experiences-sdk-core::fetchers] When resolving deep-references could not find headEntry with id '${reference.entityId}'`);
            continue;
        }
        const linkToReferent = headEntry.fields[reference.field];
        if (undefined === linkToReferent) {
            debug.debug(`[experiences-sdk-core::fetchers] Empty reference in headEntity. Probably reference is simply not set.`);
            continue;
        }
        if (!isLink(linkToReferent)) {
            debug.debug(`[experiences-sdk-core::fetchers] Non-link value in headEntity. Probably broken path '${reference.originalPath}'`);
            continue;
        }
        const linkType = linkToReferent.sys.linkType;
        if (!['Entry', 'Asset'].includes(linkType)) {
            debug.debug(`[experiences-sdk-core::fetchers] Unhandled linkType :${JSON.stringify(linkToReferent)}`);
            continue;
        }
        const referentEntity = entriesResponse.includes?.[linkType]?.find((entity) => entity.sys.id === linkToReferent.sys.id);
        if (!referentEntity) {
            debug.debug(`[experiences-sdk-core::fetchers] L2-referent ${linkType} was not found within .includes (${JSON.stringify({
                linkToReferent,
            })})`);
            continue;
        }
        linkType === 'Entry'
            ? autoFetchedReferentEntries.push(referentEntity)
            : autoFetchedReferentAssets.push(referentEntity);
    } // for (reference of deepReferences)
    return { autoFetchedReferentAssets, autoFetchedReferentEntries };
}

const MIN_FETCH_LIMIT = 1;
const fetchAllEntries = async ({ client, ids, locale, skip = 0, limit = 100, responseItems = [], responseIncludes = { Entry: [], Asset: [] }, }) => {
    try {
        if (!client) {
            throw new Error('Failed to fetch experience entities. Required "client" parameter was not provided');
        }
        if (!ids.length) {
            return {
                items: [],
                includes: {
                    Entry: [],
                    Asset: [],
                },
            };
        }
        const query = { 'sys.id[in]': ids, locale, limit, skip, include: 2 };
        const { items, includes, total: responseTotal, } = await client.withoutLinkResolution.getEntries({ ...query });
        responseItems.push(...items);
        responseIncludes?.Entry?.push(...(includes?.Entry || []));
        responseIncludes?.Asset?.push(...(includes?.Asset || []));
        // E.g Total entries = 99
        // First fetch => { skip: 0, limit: 50, total: 99 } => 50 Entries fetched in Page 0
        // Total Entries fetched = 50, 49 remaining
        // 0(skip) + 50(limit) < 99(total) => Fetch again
        // Second fetch => { skip: 50, limit: 50, total: 99 } => 49 Entries fetched in Page 1
        // Total Entries fetched = 50(Page 0) + 49(Page 1) = 99, 0 remaining
        // 50(skip) + 50(limit) > 99(total) => Stop fetching
        if (skip + limit < responseTotal) {
            return await fetchAllEntries({
                client,
                ids,
                locale,
                skip: skip + limit,
                limit,
                responseItems,
                responseIncludes,
            });
        }
        const dedupedEntries = uniqBy(responseIncludes?.Entry, (entry) => entry.sys.id);
        const dedupedAssets = uniqBy(responseIncludes?.Asset, (asset) => asset.sys.id);
        return {
            items: responseItems,
            includes: {
                Entry: dedupedEntries,
                Asset: dedupedAssets,
            },
        };
    }
    catch (error) {
        if (error instanceof Error &&
            error.message.includes('size too big') &&
            limit > MIN_FETCH_LIMIT) {
            const newLimit = Math.max(MIN_FETCH_LIMIT, Math.floor(limit / 2));
            return fetchAllEntries({
                client,
                ids,
                locale,
                skip,
                limit: newLimit,
                responseItems,
            });
        }
        throw error;
    }
};
const fetchAllAssets = async ({ client, ids, locale, skip = 0, limit = 100, responseItems = [], }) => {
    try {
        if (!client) {
            throw new Error('Failed to fetch experience entities. Required "client" parameter was not provided');
        }
        if (!ids.length) {
            return { items: [] };
        }
        const query = { 'sys.id[in]': ids, locale, limit, skip };
        const { items, total: responseTotal } = await client.getAssets({ ...query });
        responseItems.push(...items);
        if (skip + limit < responseTotal) {
            return await fetchAllAssets({
                client,
                ids,
                locale,
                skip: skip + limit,
                limit,
                responseItems,
            });
        }
        return {
            items: responseItems,
        };
    }
    catch (error) {
        if (error instanceof Error &&
            error.message.includes('size too big') &&
            limit > MIN_FETCH_LIMIT) {
            const newLimit = Math.max(MIN_FETCH_LIMIT, Math.floor(limit / 2));
            return fetchAllAssets({
                client,
                ids,
                locale,
                skip,
                limit: newLimit,
                responseItems,
            });
        }
        throw error;
    }
};

/**
 * Fetches all entries and assets from the `dataSource` of the given experience entry. This will
 * also consider deep references that are not listed explicitly but linked through deep binding paths.
 * @param options.client - Instantiated client from the Contentful SDK. If this is using the `withAllLocales` modifier, you may not provide a specific locale.
 * @param options.experienceEntry - Localized experience entry. To localize a multi locale entry, use the `localizeEntity` function.
 * @param options.locale - Retrieve a specific localized version of the referenced entities. Otherwise, it will fallback to the default locale.
 * @returns object with a list of `entries` and a list of `assets`
 */
const fetchReferencedEntities = async ({ client, experienceEntry, locale, }) => {
    if (!client) {
        throw new Error('Failed to fetch experience entities. Required "client" parameter was not provided');
    }
    if (!isExperienceEntry(experienceEntry)) {
        throw new Error('Failed to fetch experience entities. Provided "experienceEntry" does not match experience entry schema');
    }
    const deepReferences = gatherDeepReferencesFromExperienceEntry(experienceEntry);
    const entryIds = new Set();
    const assetIds = new Set();
    for (const dataBinding of Object.values(experienceEntry.fields.dataSource)) {
        if (!('sys' in dataBinding)) {
            continue;
        }
        if (dataBinding.sys.linkType === 'Entry') {
            entryIds.add(dataBinding.sys.id);
        }
        if (dataBinding.sys.linkType === 'Asset') {
            assetIds.add(dataBinding.sys.id);
        }
    }
    const [entriesResponse, assetsResponse] = await Promise.all([
        fetchAllEntries({ client, ids: [...entryIds], locale }),
        fetchAllAssets({ client, ids: [...assetIds], locale }),
    ]);
    const { autoFetchedReferentAssets, autoFetchedReferentEntries } = gatherAutoFetchedReferentsFromIncludes(deepReferences, entriesResponse);
    // Using client getEntries resolves all linked entry references, so we do not need to resolve entries in usedComponents
    const allResolvedEntries = [
        ...(entriesResponse?.items ?? []),
        ...(entriesResponse.includes?.Entry ?? []),
        ...(experienceEntry.fields.usedComponents || []),
        ...autoFetchedReferentEntries,
    ];
    const allResolvedAssets = [
        ...(assetsResponse.items ?? []),
        ...(entriesResponse?.includes?.Asset ?? []),
        ...autoFetchedReferentAssets,
    ];
    return {
        entries: allResolvedEntries,
        assets: allResolvedAssets,
    };
};

/**
 * The CMA client will automatically replace links with entry references.
 * As we're including all referenced pattern entries in usedComponents, this can lead
 * to a circular reference. This function replaces those with plain links inplace (!).
 *
 * @see https://github.com/contentful/contentful.js/issues/377
 */
const removeCircularPatternReferences = (experienceEntry, _parentIds) => {
    const parentIds = _parentIds ?? new Set([experienceEntry.sys.id]);
    const usedComponents = experienceEntry.fields.usedComponents;
    const newUsedComponents = usedComponents?.reduce((acc, linkOrEntry) => {
        if (!('fields' in linkOrEntry)) {
            // It is a link, we're good
            return [...acc, linkOrEntry];
        }
        const entry = linkOrEntry;
        if (parentIds.has(entry.sys.id)) {
            // It is an entry that already occurred -> turn it into a link to remove the circularity
            const link = {
                sys: {
                    id: entry.sys.id,
                    linkType: 'Entry',
                    type: 'Link',
                },
            };
            return [...acc, link];
        }
        // Remove circularity for its usedComponents as well (inplace)
        removeCircularPatternReferences(entry, new Set([...parentIds, entry.sys.id]));
        return [...acc, entry];
    }, []);
    // @ts-expect-error - type of usedComponents doesn't yet allow a mixed list of both links and entries
    experienceEntry.fields.usedComponents = newUsedComponents;
};
/**
 * The CMA client will automatically replace links with entry references if they are available.
 * While we're not fetching the data sources, a self reference would be replaced as the entry is
 * fetched. Any circuar reference in the object breaks SSR where we have to stringify the JSON.
 * This would fail if the object contains circular references.
 */
const removeSelfReferencingDataSource = (experienceEntry) => {
    const dataSources = experienceEntry.fields.dataSource;
    const newDataSource = Object.entries(dataSources).reduce((acc, [key, linkOrEntry]) => {
        if ('fields' in linkOrEntry && linkOrEntry.sys.id === experienceEntry.sys.id) {
            const entry = linkOrEntry;
            acc[key] = {
                sys: {
                    id: entry.sys.id,
                    linkType: 'Entry',
                    type: 'Link',
                },
            };
        }
        else {
            const link = linkOrEntry;
            acc[key] = link;
        }
        return acc;
    }, {});
    experienceEntry.fields.dataSource = newDataSource;
};

/**
 * Attaches the default prebinding value (if any) to the experience entry's dataSource.
 *
 * This ensures that any default values defined in pattern property definitions are included
 * in the dataSource, so that linked entities can be fetched and resolved correctly.
 * Without this, defaults may be omitted, resulting in unresolved references during binding.
 *
 * @returns The number of sideloaded default values, or false if the entry is not a pattern.
 */
const sideloadPrebindingDefaultValues = (patternEntry) => {
    let sideloadedCount = 0;
    const addDefaultValueToDataSource = (_definitionId, definition) => {
        if (!definition.defaultSource) {
            // prebinding preset doesn't have default value, which is perfectly fine
            return;
        }
        const { contentTypeId, link, type, // At the time of writing, it means that defaultSource is a link to a Contentful Entry, but in the future it could be a link to third party resource or smth.
        // this .type enumeration will not have the same meaning as `Link#sys#linkType`.
         } = definition.defaultSource;
        if (!isLink(link)) {
            // default value is not a link, maybe due to a bug
            return;
        }
        if (!type || !contentTypeId) {
            // broken data structure, unlikely to happen, only due to a bug
            return;
        }
        // Throw in the link to the default-entry into the dataSource, this way
        // we rely on the mechanism of fetchReferencedEntities() to "sideload" them.
        // Keep in mind that dataSource will be available as EntityStore.dataSource
        // and now will contain also key for `sideloaded_uuid` entry.
        // When "sideloading" entries into the entityStore, we must ensure that
        // there's corresponding entry in the dataSource, because all bound variables
        // are resolved, via path that is indirectly referencing the dataSource,
        // eg. { type: 'BoundValue', path: '/sideloaded_uuid/fields/title' }
        patternEntry.fields.dataSource = {
            ...patternEntry.fields.dataSource,
            [`${SIDELOADED_PREFIX}${link.sys.id}`]: {
                // to highlight that this is a sideloaded entry, we prefix it
                sys: {
                    type: 'Link',
                    linkType: link.sys.linkType,
                    id: link.sys.id,
                },
            },
        };
        sideloadedCount++;
    };
    if (!checkIsAssemblyEntry(patternEntry)) {
        // Only supported for pattern entries since experience entries don't define pattern properties.
        return false;
    }
    // --------------------
    // Sideload prebinding values for the L1 parent pattern aka `pA`
    // --------------------
    const definitions = patternEntry.fields.componentSettings?.prebindingDefinitions?.[0].parameterDefinitions ?? {};
    Object.entries(definitions).forEach(([id, definition]) => {
        addDefaultValueToDataSource(id, definition);
    });
    // --------------------
    // Sideload all default values for the L2 nested patterns, patterns aka`pB`
    // --------------------
    const nestedPatternEntriesLevel2 = (patternEntry.fields.usedComponents || []).filter((component) => component !== undefined && checkIsAssemblyEntry(component));
    nestedPatternEntriesLevel2.forEach((patternEntry) => {
        const parameterDefinitions = patternEntry.fields.componentSettings?.prebindingDefinitions?.[0].parameterDefinitions ?? {};
        Object.entries(parameterDefinitions).forEach(([id, definition]) => {
            addDefaultValueToDataSource(id, definition);
        });
    });
    // --------------------
    // Sideload all default values for the L3 nested patterns, patterns aka `pC`
    // --------------------
    const nestedPatternEntriesLevel3 = nestedPatternEntriesLevel2.flatMap((patternEntryLevel2) => {
        const usedComponents = patternEntryLevel2.fields.usedComponents || [];
        const filteredUsedComponents = usedComponents.filter((component) => component !== undefined && checkIsAssemblyEntry(component));
        return filteredUsedComponents;
    });
    nestedPatternEntriesLevel3.forEach((patternEntry) => {
        const parameterDefinitions = patternEntry.fields.componentSettings?.prebindingDefinitions?.[0].parameterDefinitions ?? {};
        Object.entries(parameterDefinitions).forEach(([id, definition]) => {
            addDefaultValueToDataSource(id, definition);
        });
    });
    return sideloadedCount;
};

/**
 * Run additional checks on the references used in the experience entry and
 * process data required for prebinding. This must be used after fetching an
 * experience entry.
 *
 * The changes made to the passed experience entry are done inplace.
 **/
const prepareExperienceEntry = (experienceEntry) => {
    removeCircularPatternReferences(experienceEntry);
    removeSelfReferencingDataSource(experienceEntry);
    sideloadPrebindingDefaultValues(experienceEntry);
};

const errorMessagesWhileFetching$1 = {
    experience: 'Failed to fetch experience',
    experienceReferences: 'Failed to fetch entities, referenced in experience',
};
const handleError$1 = (generalMessage, error) => {
    const message = error instanceof Error ? error.message : `Unknown error: ${error}`;
    throw Error(message);
};
/**
 * Fetches an experience entry by its slug and additionally fetches all its references to return
 * an initilized experience instance.
 * @param {FetchBySlugParams} options - options to fetch the experience
 */
async function fetchBySlug({ client, experienceTypeId, slug, localeCode, isEditorMode, }) {
    // Be a no-op if in editor mode
    if (isEditorMode)
        return;
    let experienceEntry = undefined;
    if (!localeCode) {
        throw new Error('Failed to fetch by slug. Required "localeCode" parameter was not provided');
    }
    try {
        experienceEntry = await fetchExperienceEntry({
            client,
            experienceTypeId,
            locale: localeCode,
            identifier: {
                slug,
            },
        });
        if (!experienceEntry) {
            throw new Error(`No experience entry with slug: ${slug} exists`);
        }
        prepareExperienceEntry(experienceEntry);
        try {
            const { entries, assets } = await fetchReferencedEntities({
                client,
                experienceEntry,
                locale: localeCode,
            });
            const experience = createExperience({
                experienceEntry,
                referencedAssets: assets,
                referencedEntries: entries,
                locale: localeCode,
            });
            return experience;
        }
        catch (error) {
            handleError$1(errorMessagesWhileFetching$1.experienceReferences, error);
        }
    }
    catch (error) {
        handleError$1(errorMessagesWhileFetching$1.experience, error);
    }
}

const errorMessagesWhileFetching = {
    experience: 'Failed to fetch experience',
    experienceReferences: 'Failed to fetch entities, referenced in experience',
};
const handleError = (generalMessage, error) => {
    const message = error instanceof Error ? error.message : `Unknown error: ${error}`;
    throw Error(message);
};
/**
 * Fetches an experience entry by its id and additionally fetches all its references to return
 * an initilized experience instance.
 * @param {FetchByIdParams} options - options to fetch the experience
 */
async function fetchById({ client, experienceTypeId, id, localeCode, isEditorMode, }) {
    //Be a no-op if in editor mode
    if (isEditorMode)
        return;
    let experienceEntry = undefined;
    if (!localeCode) {
        throw new Error('Failed to fetch by id. Required "localeCode" parameter was not provided');
    }
    try {
        experienceEntry = await fetchExperienceEntry({
            client,
            experienceTypeId,
            locale: localeCode,
            identifier: {
                id,
            },
        });
        if (!experienceEntry) {
            throw new Error(`No experience entry with id: ${id} exists`);
        }
        prepareExperienceEntry(experienceEntry);
        try {
            const { entries, assets } = await fetchReferencedEntities({
                client,
                experienceEntry,
                locale: localeCode,
            });
            const experience = createExperience({
                experienceEntry,
                referencedAssets: assets,
                referencedEntries: entries,
                locale: localeCode,
            });
            return experience;
        }
        catch (error) {
            handleError(errorMessagesWhileFetching.experienceReferences, error);
        }
    }
    catch (error) {
        handleError(errorMessagesWhileFetching.experience, error);
    }
}

export { BREAKPOINTS_STRATEGY_DESKTOP_FIRST, BREAKPOINTS_STRATEGY_MOBILE_FIRST, DebugLogger, DeepReference, EditorModeEntityStore, EntityStore, EntityStoreBase, MEDIA_QUERY_REGEXP, VisualEditorMode, addLocale, addMinHeightForEmptyStructures, breakpointsRegistry, buildCfStyles, buildStyleTag, buildTemplate, builtInStyles, calculateNodeDefaultHeight, checkIsAssemblyDefinition, checkIsAssemblyEntry, checkIsAssemblyNode, columnsBuiltInStyles, containerBuiltInStyles, createExperience, debug, defineBreakpoints, defineDesignTokens, defineSdkOptions, designTokensRegistry, detachExperienceStyles, detectBreakpointsStrategy, disableDebug, dividerBuiltInStyles, doesMismatchMessageSchema, enableDebug, extractLeafLinksReferencedFromExperience, extractReferencesFromEntries, extractReferencesFromEntriesAsIds, fetchAllAssets, fetchAllEntries, fetchById, fetchBySlug, fetchExperienceEntry, fetchReferencedEntities, findOutermostCoordinates, flattenDesignTokenRegistry, gatherDeepReferencesFromExperienceEntry, gatherDeepReferencesFromTree, generateRandomId, getActiveBreakpointIndex, getBreakpointRegistration, getDataFromTree, getDesignTokenRegistration, getElementCoordinates, getFallbackBreakpointIndex, getPrebindingPathBySourceEntry, getSdkOptions, getTargetValueInPixels, getTemplateValue, getValueForBreakpoint, inMemoryEntities, inMemoryEntitiesStore, indexByBreakpoint, isArrayOfLinks, isAsset, isCfStyleAttribute, isComponentAllowedOnRoot, isContentfulComponent, isContentfulStructureComponent, isDeepPath, isDeepPrebinding, isElementHidden, isEntry, isExperienceEntry, isLink, isLinkToAsset, isLinkToEntry, isPatternComponent, isPatternEntry, isPreboundProp, isStructureWithRelativeHeight, isValidBreakpointValue, lastPathNamedSegmentEq, localizeEntity, maybePopulateDesignTokenValue, mediaQueryMatcher, mergeDesignValuesByBreakpoint, optionalBuiltInStyles, parseCSSValue, parseDataSourcePathIntoFieldset, parseDataSourcePathWithL1DeepBindings, referencesOf, resetBreakpointsRegistry, resetDesignTokenRegistry, resolveBackgroundImageBinding, resolveHyperlinkPattern, runBreakpointsValidation, sanitizeNodeProps, sdkOptionsRegistry, sectionBuiltInStyles, sendMessage, setDebugLevel, singleColumnBuiltInStyles, splitDirectAndSlotChildren, stringifyCssProperties, toCSSAttribute, toMediaQuery, transformBoundContentValue, transformVisibility, treeMap, treeVisit, tryParseMessage, uniqueById, useInMemoryEntities, validateExperienceBuilderConfig };
//# sourceMappingURL=index.js.map
